import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    TabContent,
    TabPane,
    Spinner,
    Button,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { enqueueSnackbar } from 'notistack';
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import CategoryInfiniteScroll from "../../../components/CategoryInfiniteScroll/CategoryInfiniteScroll";
import { 
    getAllAssets, 
    getAllBuildings,
    getAllBuildingsWithDetails, 
    getAllEquipments,
    getAllFurniture,
    getAllICT,
    getAllLand,
    getAllMachinery,
    getAllVehicles,
    getAllInfrastructure,
    getAllSiteWorksWithDetails,
    getSiteWorks,
    getAllAssetValuations,
    getAssetsByValuation,
    getBarcodePdfFiles,
    getAllAstInstitutions,
    getAssetsByCategory,
    searchAssets,
    getAssetBarcodes,
    combineAssetInstitution,
} from "../../../services/ValitanApiService";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../utils/exportUtils";
import {
    resolveAssetNameDisplay,
    resolveBuildingNameDisplay,
} from "../../../utils/otherValueDisplay";
import { resolveBackendUrl } from "../../../helpers/serverUrl";
import logo from "../../../assets/images/logo.png";

import CombineHistoryModal from "./components/CombineHistoryModal";
import AssetImportModal from "./components/AssetImportModal";

// Import all asset category tabs
import BuildingsTab from "./Tabs/BuildingsTab";
import EquipmentTab from "./Tabs/EquipmentTab";
import FurnitureTab from "./Tabs/FurnitureTab";
import ICTTab from "./Tabs/ICTTab";
import LandTab from "./Tabs/LandTab";
import MachineryTab from "./Tabs/MachineryTab";
import VehiclesTab from "./Tabs/VehiclesTab";
import AssetsTab from "./Tabs/AssetsTab"; // All assets tab
import SiteWorksTab from "./Tabs/SiteWorksTab";
import InfrastructureTab from "./Tabs/InfrastructureTab";

const BRAND_BLUE = "#0147C8";
const DEFAULT_PAGE_SIZE = 10;

const CATEGORY_CODE_MAP = {
    all: null, // Special case: fetch all categories
    buildings: "BLDG",
    equipment: "EQP",
    furniture: "FURN",
    ict: "ICT",
    land: "LAND",
    machinery: "MACH",
    vehicles: "VEH",
    infrastructure: "INFR",
    siteworks: "SITE",
};

const TAB_KEYS = Object.keys(CATEGORY_CODE_MAP);

// Category codes for fetching all assets (exclude "all" itself)
// const ALL_CATEGORY_CODES = ["BLDG", "EQP", "FURN", "ICT", "LAND", "MACH", "VEH", "SITE"];

const BARCODE_FILE_PREFIX_BY_TAB = {
    buildings: "bldg",
    equipment: "eqp",
    furniture: "furn",
    ict: "ict",
    land: "land",
    machinery: "mach",
    vehicles: "veh",
    infrastructure: "infr",
    siteworks: "site",
};

const getCategoryLabel = (tabKey) => {
    if (tabKey === "all") return "All";
    if (tabKey === "siteworks") return "Site Works";
    if (tabKey === "infrastructure") return "Infrastructure";
    return tabKey.charAt(0).toUpperCase() + tabKey.slice(1);
};

const resolveInstitutionName = (institution) =>
    String(institution?.name || institution?.institution_name || institution?.institutionName || "").trim();

const resolveInstitutionBracket = (record) =>
    String(
        record?.institution_category ||
        record?.institutionCategory ||
        record?.category_name ||
        record?.categoryName ||
        record?.type ||
        ""
    ).trim();

const formatInstitutionWithBracket = (name, bracket) => {
    const cleanName = String(name || "").trim() || "-";
    const cleanBracket = String(bracket || "").trim();
    return cleanBracket ? `${cleanName} (${cleanBracket})` : cleanName;
};

const getAssetQuantity = (asset) => {
    const value = asset?.quantity ?? asset?.qty ?? asset?.count ?? 1;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getBarcodeRange = (barcode, quantity) => {
    if (!barcode || barcode === "-" || !quantity || quantity <= 1) {
        return { display: barcode || "-", isRange: false, start: barcode, end: barcode };
    }
    const match = String(barcode).match(/^(.*?)(\d+)$/);
    if (!match) {
        return { display: barcode, isRange: false, start: barcode, end: barcode };
    }
    const prefix = match[1];
    const numStr = match[2];
    const startNum = Number.parseInt(numStr, 10);
    const endNum = startNum + quantity - 1;
    const endBarcode = prefix + String(endNum).padStart(numStr.length, "0");
    return { display: `${barcode} - ${endBarcode}`, isRange: true, start: barcode, end: endBarcode };
};

const getAssetPrimaryBarcode = (asset) =>
    String(asset?.barcode || asset?.barcode_label || asset?.barcodeLabel || "").trim();

const getAssetDisplayName = (asset) => {
    const name = String(asset?.name || asset?.assetName || asset?.asset_name || "").trim();
    const otherName = String(
        asset?.name_other ||
        asset?.nameOther ||
        asset?.other_name ||
        asset?.otherName ||
        asset?.asset?.other_name ||
        asset?.asset?.otherName ||
        ""
    ).trim();
    if ((name.toUpperCase() === "OTHERS" || name.toUpperCase() === "OTHER") && otherName) {
        return otherName;
    }
    return name || otherName || "-";
};

const getAssetCategoryDisplay = (asset) => {
    const category = asset?.asset_category || asset?.assetCategory || asset?.asset?.asset_category || asset?.asset?.assetCategory;
    const name = String(
        asset?.category_name ||
        asset?.categoryName ||
        asset?.asset_category_name ||
        asset?.assetCategoryName ||
        (category && typeof category === 'object' ? category.name : category) ||
        asset?.asset_category_code ||
        asset?.assetCategoryCode ||
        asset?.category_code ||
        asset?.categoryCode ||
        (category && typeof category === 'object' ? category.code : "") ||
        ""
    ).trim();
    return name || "-";
};

const getAssetWardDisplay = (asset) => {
    const ward = asset?.ward || asset?.asset?.ward || {};
    return String(
        asset?.ward_name ||
        asset?.wardName ||
        asset?.institution_ward_name ||
        asset?.institutionWardName ||
        ward?.name ||
        asset?.location ||
        ""
    ).trim() || "-";
};

const getPageContent = (response) => {
    const payload = response?.data?.data ?? response?.data ?? {};
    return Array.isArray(payload) ? payload : (payload.content || payload.data || []);
};

const getPageMeta = (response, fallbackSize) => {
    const payload = response?.data?.data ?? response?.data ?? {};
    const pagination = response?.data?.pagination || payload?.pagination || {};
    const totalElements = payload.totalElements ?? payload.total_elements ?? pagination.totalElements ?? pagination.total_elements ?? 0;
    const totalPages = payload.totalPages ?? payload.total_pages ?? pagination.totalPages ?? pagination.total_pages ?? Math.max(1, Math.ceil(totalElements / fallbackSize));
    return {
        totalElements,
        totalPages,
    };
};

const resolveServerFileUrl = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    return resolveBackendUrl(url);
};
const makeInitialPaginationByTab = () =>
    TAB_KEYS.reduce((acc, key) => {
        acc[key] = { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE };
        return acc;
    }, {});

    const AssetsByCategory = () => {
        const ComingSoon = ({ title = "Coming Soon" }) => (
            <Container fluid className="mt-4">
                <Breadcrumbs title="Valuations" breadcrumbItem={title} />
                <Row>
                <Col lg={12}>
                    <Card className="text-center">
                        <CardBody className="py-5">
                            <h2 className="mb-2">{title}</h2>
                            <p className="text-muted mb-0">
                                We're preparing this section. Please check back soon.
                            </p>
                        </CardBody>
                    </Card>
                </Col>
                </Row>
            </Container>
        );

        const isDataAnalysisComingSoon = false;
    const [searchParams] = useSearchParams();
    const categoryParam = searchParams.get("category") || "all";
    const combineParam = searchParams.get("combine") === "true";
    const assetIdParam = searchParams.get("assetId") || "";
    const valuationIdParam = searchParams.get("valuationId") || "";
    
    // Validate category param and default to "all" if invalid
    const initialTab = TAB_KEYS.includes(categoryParam) ? categoryParam : "all";
    
    const [activeTab, setActiveTab] = useState(initialTab); // Default to "all" tab or from URL
    const [assetsByCategory, setAssetsByCategory] = useState({}); // Store ALL loaded assets per category (accumulated)
    const [loadingByCategory, setLoadingByCategory] = useState({}); // Loading state per category
    const [loadingMoreByCategory, setLoadingMoreByCategory] = useState({}); // Loading more state
    const [paginationByTab, setPaginationByTab] = useState(makeInitialPaginationByTab);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(""); // Search functionality
    
    // Column selection for export (modal + export type only)
    const [columnModalOpen, setColumnModalOpen] = useState(false);
    const [pendingExportType, setPendingExportType] = useState(null); // 'csv', 'excel', or 'pdf'
    const [isFetchingAllData, setIsFetchingAllData] = useState(false); // Loading state for fetching all data
    const [exportHeaders, setExportHeaders] = useState([]); // Store dynamically generated export headers
    const [barcodePdfModalOpen, setBarcodePdfModalOpen] = useState(false);
    const [loadingBarcodePdfs, setLoadingBarcodePdfs] = useState(false);
    const [barcodePdfsByCategory, setBarcodePdfsByCategory] = useState({});
    const [selectedAssetIds, setSelectedAssetIds] = useState(new Set());
    const [selectedAssetsData, setSelectedAssetsData] = useState(new Map());
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [combineInstitutionModalOpen, setCombineInstitutionModalOpen] = useState(false);
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [selectedTargetInstitutionId, setSelectedTargetInstitutionId] = useState("");
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);
    const [combiningInstitution, setCombiningInstitution] = useState(false);
    const [combineModalDismissed, setCombineModalDismissed] = useState(false);
    const [combineCategory, setCombineCategory] = useState(initialTab);
    const [combineAssets, setCombineAssets] = useState([]);
    const [combineAssetPage, setCombineAssetPage] = useState(0);
    const [combineAssetTotalPages, setCombineAssetTotalPages] = useState(1);
    const [combineAssetTotalElements, setCombineAssetTotalElements] = useState(0);
    const [loadingCombineAssets, setLoadingCombineAssets] = useState(false);
    const [loadingAllCombineAssets, setLoadingAllCombineAssets] = useState(false);
    const [combineHistoryModalOpen, setCombineHistoryModalOpen] = useState(false);
    const [combineSearch, setCombineSearch] = useState("");
    const [combineInstitutionFilterId, setCombineInstitutionFilterId] = useState("");
    const [combineBarcodeFilter, setCombineBarcodeFilter] = useState("all");
    const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
    const [barcodeModalAsset, setBarcodeModalAsset] = useState(null);
    const [barcodeModalRows, setBarcodeModalRows] = useState([]);
    const [loadingBarcodeRows, setLoadingBarcodeRows] = useState(false);

    const institutionSelectOptions = useMemo(() => (
        institutionOptions
            .map((institution) => {
                let label = formatInstitutionWithBracket(
                    resolveInstitutionName(institution),
                    resolveInstitutionBracket(institution)
                );
                const wardDisplay = getAssetWardDisplay(institution);
                if (wardDisplay && wardDisplay !== "-") {
                    label += ` [${wardDisplay}]`;
                }
                return {
                    value: institution.id,
                    label: label,
                };
            })
            .filter((option) => option.value && option.label)
    ), [institutionOptions]);

    const selectedTargetInstitutionOption = useMemo(() => (
        institutionSelectOptions.find((option) => option.value === selectedTargetInstitutionId) || null
    ), [institutionSelectOptions, selectedTargetInstitutionId]);

    const categorySelectOptions = useMemo(() => (
        TAB_KEYS.map((key) => ({
            value: key,
            label: getCategoryLabel(key),
        }))
    ), []);

    const selectedCombineCategoryOption = useMemo(() => (
        categorySelectOptions.find((option) => option.value === combineCategory) || categorySelectOptions[0]
    ), [categorySelectOptions, combineCategory]);

    const combineInstitutionFilterOptions = useMemo(() => {
        const byName = new Map();
        combineAssets.forEach((asset) => {
            const name = asset.institution_name || asset.institutionName || "";
            const bracket = resolveInstitutionBracket(asset);
            const label = formatInstitutionWithBracket(name, bracket);
            if (name && !byName.has(label)) {
                byName.set(label, { value: label, label });
            }
        });
        return Array.from(byName.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [combineAssets]);

    const selectedCombineInstitutionFilterOption = useMemo(() => (
        combineInstitutionFilterOptions.find((option) => option.value === combineInstitutionFilterId) || null
    ), [combineInstitutionFilterOptions, combineInstitutionFilterId]);

    // Get export headers based on category - includes ALL submitted fields
    // Helper function to convert field key to readable label
    const formatFieldLabel = (key) => {
        // Handle special cases
        if (key === "serialNumber") return "#";
        
        // Convert snake_case to Title Case
        return key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Helper function to check if a field should be excluded (id fields and raw condition)
    const shouldExcludeField = (key) => {
        // Exclude fields that end with _id or are just "id"
        const idPattern = /^id$|_id$/;
        // Also exclude observed_condition (raw UUID) - we use observed_condition_name instead
        return idPattern.test(key) || key === 'observed_condition';
    };

    // Helper function to determine if a field should be selected by default
    const isDefaultSelected = (key, category) => {
        // Common defaults visible in almost all tables
        const baseDefaults = [
            "serialNumber",
            "category_name",
            "barcode",
            "name",
            "description",
            "quantity",
            "observed_condition_name",
            "remark",
            "reference_number",
            "status",
        ];

        // Category-specific defaults – align with what is shown on each tab
        const categoryDefaults = {
            siteworks: [
                "site_work_type",
                "dimensions",
                "location",
                "ward_name",
                "institution_name",
            ],
            equipment: [
                "make",
                "model",
                "location",
                "valuer_name",
            ],
            buildings: [
                "building_number",
                "location",
                "ward_name",
                "reference_number",
                "status",
            ],
            land: [
                "plot_number",
                "block_number",
                "location",
                "ward_name",
            ],
            infrastructure: [
                "type_of_bridge",
                "width_m",
                "span_m",
                "street",
                "ward_name",
                "current_replacement_cost",
                "fair_value",
            ],
            vehicles: [
                "registration_number",
                "chassis_number",
                "engine_number",
                "make",
                "model",
                "location",
            ],
            machinery: [
                "registration_number",
                "chassis_number",
                "engine_number",
                "make",
                "model",
                "location",
            ],
            furniture: [
                "location",
                "ward_name",
                "institution_name",
            ],
            ict: [
                "location",
                "ward_name",
                "institution_name",
            ],
        };

        const cat = categoryDefaults[category] || [];
        return baseDefaults.includes(key) || cat.includes(key);
    };

    const getExportHeaders = (category, data = []) => {
        // If data is provided, dynamically generate headers from all fields
        if (data && data.length > 0) {
            // Collect all unique keys from all assets
            const allKeys = new Set();
            data.forEach(asset => {
                Object.keys(asset).forEach(key => {
                    // Only include if not an id / excluded field
                    if (!shouldExcludeField(key)) {
                        const value = asset[key];
                        // Include primitive types (string, number, boolean, null, undefined)
                        // Include arrays (will be stringified during export)
                        // Exclude only plain objects (not arrays, not null)
                        if (value === null || 
                            value === undefined || 
                            typeof value !== 'object' || 
                            Array.isArray(value)) {
                            allKeys.add(key);
                        }
                    }
                });
            });

            // Apply category-specific filtering so non-building categories don't show building-only fields
            const filteredKeys = Array.from(allKeys).filter((key) => {
                if (category === "buildings" || category === "all") {
                    return true;
                }

                // Hide purely building/room specific fields for other categories
                const buildingSpecificKeys = new Set([
                    "building_name",
                    "other_building_name",
                    "building_number",
                    "number_of_buildings",
                    "floor",
                    "room_name_or_number",
                    "room_others",
                    "building_use",
                    "building_use_name",
                    "building_use_code",
                ]);

                if (buildingSpecificKeys.has(key)) {
                    return false;
                }

                return true;
            });

            // Convert to array of header objects, with serialNumber, building_serial_no/site_work_serial_no, and reference_number first
            const headers = filteredKeys
                .sort((a, b) => {
                    // Put serialNumber first - always
                    if (a === "serialNumber") return -1;
                    if (b === "serialNumber") return 1;
                    // Put building_serial_no second (for buildings) - always
                    if (a === "building_serial_no") return -1;
                    if (b === "building_serial_no") return 1;
                    // Put site_work_serial_no second (for siteworks) - always
                    if (a === "site_work_serial_no") return -1;
                    if (b === "site_work_serial_no") return 1;
                    // Put reference_number third - always
                    if (a === "reference_number") return -1;
                    if (b === "reference_number") return 1;
                    return a.localeCompare(b);
                })
                .map(key => ({
                    key,
                    label: formatFieldLabel(key),
                    defaultSelected: isDefaultSelected(key, category)
                }));

            return headers;
        }

        // Fallback: return basic headers if no data provided
        return [
            { key: "serialNumber", label: "#", defaultSelected: isDefaultSelected("serialNumber", category) },
            { key: "category_name", label: "Category", defaultSelected: isDefaultSelected("category_name", category) },
            { key: "barcode", label: "Barcode", defaultSelected: isDefaultSelected("barcode", category) },
            { key: "name", label: "Name", defaultSelected: isDefaultSelected("name", category) },
            { key: "description", label: "Description", defaultSelected: isDefaultSelected("description", category) },
            { key: "quantity", label: "Quantity", defaultSelected: isDefaultSelected("quantity", category) },
            { key: "observed_condition_name", label: "Condition", defaultSelected: isDefaultSelected("observed_condition_name", category) },
            { key: "remark", label: "Remark", defaultSelected: isDefaultSelected("remark", category) },
        ];
    };

    // Fetch ALL assets for export (ignoring pagination)
    const fetchAllAssetsForExport = useCallback(async (tabKey) => {
        try {
            let allAssets = [];
            
            // Handle "all" category - fetch all assets
            if (tabKey === "all") {
                let pageNumber = 0;
                const pageSize = 1000; // Use large page size to minimize requests
                let hasMore = true;
                
                while (hasMore) {
                    const res = await getAllAssets(pageNumber, pageSize);
                    const pageData = res.data?.data || res.data || {};
                    // Spring Data Page object has 'content' array, or direct array
                    const data = pageData.content || pageData.data || pageData || [];
                    
                    // If no data returned, break to prevent infinite loop
                    if (!data || data.length === 0) {
                        break;
                    }
                    
                    allAssets = [...allAssets, ...data];
                    
                    // Check both camelCase and snake_case for pagination
                    const totalPages = pageData.totalPages || pageData.total_pages || 1;
                    // If we got less data than pageSize, we're on the last page
                    if (data.length < pageSize) {
                        hasMore = false;
                    } else {
                        // Continue if there are more pages
                        hasMore = pageNumber < totalPages - 1;
                    }
                    
                    pageNumber++;
                }
            } else if (tabKey === "siteworks") {
                // Handle siteworks - use optimized native SQL query endpoint
                // Export should match EXACTLY what the SQL query returns
                const res = await getAllSiteWorksWithDetails();
                const siteWorks = res.data?.data || res.data || [];
                
                // Use the exact fields from the query without additional transformations
                // The query returns (in order):
                // 1. site_work_serial_no
                // 2. asset_category
                // 3. asset_name
                // 4. asset_description (from a.description)
                // 5. ward
                // 6. institution_category
                // 7. institution_name
                // 8. plot_area
                // 9. size_unit
                // 10. land_use
                // 11. has_water
                // 12. has_electricity
                // 13. latitude
                // 14. longitude
                // 15. site_work_type
                // 16. description (from sw.description)
                // 17. dimensions
                // 18. serial_number
                // 19. observed_condition
                // 20. site_work_remark
                // 21. photo_urls
                // 22. valuation_ref
                // 23. valuation_date
                // 24. valuation_status
                // 25. valuer_name
                // 26. asset_remark
                const transformed = siteWorks.map(sw => {
                    // Include all important fields for site works export
                    return {
                        // Serial numbers
                        asset_serial_no: sw.site_work_serial_no || sw.siteWorkSerialNo || null,
                        site_work_serial_no: sw.site_work_serial_no || sw.siteWorkSerialNo || null,
                        
                        // Asset basic info
                        asset_category: sw.asset_category || "",
                        asset_name: sw.asset_name || "",
                        other_name: sw.other_specify || sw.otherName || "",
                        description: sw.description || sw.asset_description || "",
                        asset_description: sw.asset_description || "",
                        
                        // Location
                        ward: sw.ward || "",
                        institution_category: sw.institution_category || "",
                        institution_name: sw.institution_name || "",
                        
                        // Asset details
                        quantity: 1, // Default for site works
                        observed_condition: sw.observed_condition || "",
                        acquisition_date: sw.acquisition_date || null,
                        useful_life_span_years: sw.useful_life_span_years || null,
                        
                        // Land/Site info
                        plot_area: sw.plot_area || null,
                        size_unit: sw.size_unit || "",
                        land_use: sw.land_use || "",
                        latitude: sw.latitude || null,
                        longitude: sw.longitude || null,
                        
                        // Vehicle/Equipment fields (may not exist for site works, but include for compatibility)
                        make: sw.make || "",
                        model: sw.model || "",
                        serial_number: sw.serial_number || "",
                        registration_number: sw.registration_number || "",
                        year_of_manufacture: sw.year_of_manufacture || null,
                        engine_capacity: sw.engine_capacity || null,
                        fuel_type: sw.fuel_type || "",
                        body_type: sw.body_type || "",
                        body_type_others: sw.body_type_others || "",
                        
                        // Site work specific
                        site_work_type: sw.site_work_type || "",
                        dimensions: sw.dimensions || "",
                        has_water: sw.has_water || false,
                        has_electricity: sw.has_electricity || false,
                        
                        // Photos
                        photo_urls: Array.isArray(sw.photo_urls) 
                            ? sw.photo_urls 
                            : (sw.photo_urls ? [sw.photo_urls] : []),
                        
                        // Valuation
                        valuation_ref: sw.valuation_ref || "",
                        valuation_date: sw.valuation_date || "",
                        valuation_status: sw.valuation_status || "",
                        valuer_name: sw.valuer_name || "",
                        
                        // Remarks
                        remark: sw.site_work_remark || sw.asset_remark || "",
                        site_work_remark: sw.site_work_remark || "",
                        asset_remark: sw.asset_remark || "",
                    };
                });
                
                allAssets = [...allAssets, ...transformed];
            } else if (tabKey === "buildings") {
                // Handle buildings - use optimized native SQL query endpoint
                const res = await getAllBuildingsWithDetails();
                const buildings = res.data?.data || res.data || [];
                
                // Transform BuildingQueryResultDTO to match AssetDTO structure for export/display
                // Include all important fields for buildings export
                const transformed = buildings.map(building => {
                        const resolvedBuildingName = resolveBuildingNameDisplay(building, "");
                        // Include all important fields explicitly
                        const result = {
                            ...building,
                            
                            // Asset basic info
                            asset_category: building.asset_category || building.assetCategory || "",
                            asset_name: building.asset_name || building.assetName || "",
                            description: building.description || "",
                            ward: building.ward || "",
                            institution_category: building.institution_category || building.institutionCategory || "",
                            institution_name: building.institution_name || building.institutionName || "",
                            
                            // Land/Site info
                            plot_area: building.plot_area || building.plotArea || null,
                            size_unit: building.size_unit || building.sizeUnit || "",
                            land_use: building.land_use || building.landUse || "",
                            number_of_buildings: building.number_of_buildings || building.numberOfBuildings || null,
                            has_water: building.has_water || building.hasWater || false,
                            has_electricity: building.has_electricity || building.hasElectricity || false,
                            latitude: building.latitude || null,
                            longitude: building.longitude || null,
                            
                            // Building details
                            building_name: resolvedBuildingName || building.building_name || building.buildingName || "",
                            building_type: building.building_type || building.buildingType || "",
                            year_built: building.year_built || building.yearBuilt || null,
                            no_of_floors: building.no_of_floors || building.noOfFloors || null,
                            use_status: building.use_status || building.useStatus || "",
                            completion_rate: building.completion_rate || building.completionRate || null,
                            useful_life_span_years: building.useful_life_span_years || building.usefulLifeSpanYears || null,
                            building_measurement: building.building_measurement || building.buildingMeasurement || "",
                            general_remark: building.general_remark || building.generalRemark || "",
                            observed_condition: building.observed_condition || building.observedCondition || "",
                            
                            // Images
                            sketch_image_url: building.sketch_image_url || building.sketchImageUrl || "",
                            photo_urls: Array.isArray(building.photo_urls) 
                                ? building.photo_urls 
                                : (building.photo_urls ? [building.photo_urls] : []),
                            
                            // Valuation
                            valuation_ref: building.valuation_ref || building.valuationRef || "",
                            valuation_date: building.valuation_date || building.valuationDate || "",
                            valuation_status: building.valuation_status || building.valuationStatus || "",
                            fair_value: building.fair_value || building.fairValue || null,
                            valuer_name: building.valuer_name || building.valuerName || "",
                            
                            // Remarks
                            remark: building.remark || building.general_remark || building.generalRemark || "",
                            
                            // Add computed/aliased fields for compatibility
                            name: building.asset_name || building.assetName || resolvedBuildingName || building.building_name || building.buildingName || "-",
                            category_name: building.asset_category || building.assetCategory || "Buildings",
                            ward_name: building.ward || "",
                            location: building.ward || "-",
                            plot_size: building.plot_area || building.plotArea || null, // Alias for compatibility
                            quantity: 1, // Default for buildings
                            
                            // Add aliases for reference number and status for easier identification
                            reference_number: building.valuation_ref || building.valuationRef || "",
                            status: building.valuation_status || building.valuationStatus || "",
                        };
                        
                        return result;
                    });
                    
                    allAssets = [...allAssets, ...transformed];
            } else if (tabKey === "equipment") {
                // Handle equipment - use Equipment API
                let pageNumber = 0;
                const pageSize = 1000;
                let hasMore = true;
                
                while (hasMore) {
                    const res = await getAllEquipments(pageNumber, pageSize);
                    const pageData = res.data?.data || res.data || {};
                    const data = pageData.content || pageData.data || pageData || [];
                    
                    // If no data returned, break to prevent infinite loop
                    if (!data || data.length === 0) {
                        break;
                    }
                    
                    allAssets = [...allAssets, ...data];
                    
                    // Check both camelCase and snake_case for pagination
                    const totalPages = pageData.totalPages || pageData.total_pages || 1;
                    
                    // If we got less data than pageSize, we're on the last page
                    if (data.length < pageSize) {
                        hasMore = false;
                    } else {
                        // Continue if there are more pages
                        hasMore = pageNumber < totalPages - 1;
                    }
                    
                    pageNumber++;
                }
            } else if (tabKey === "furniture") {
                // Handle furniture - use Furniture API
                let pageNumber = 0;
                const pageSize = 1000;
                let hasMore = true;
                
                while (hasMore) {
                    const res = await getAllFurniture(pageNumber, pageSize);
                    const pageData = res.data?.data || res.data || {};
                    const data = pageData.content || pageData.data || pageData || [];
                    
                    // If no data returned, break to prevent infinite loop
                    if (!data || data.length === 0) {
                        break;
                    }
                    
                    allAssets = [...allAssets, ...data];
                    
                    // Check both camelCase and snake_case for pagination
                    const totalPages = pageData.totalPages || pageData.total_pages || 1;
                    
                    // If we got less data than pageSize, we're on the last page
                    if (data.length < pageSize) {
                        hasMore = false;
                    } else {
                        // Continue if there are more pages
                        hasMore = pageNumber < totalPages - 1;
                    }
                    
                    pageNumber++;
                }
            } else if (tabKey === "ict") {
                // Handle ICT - use ICT API
                let pageNumber = 0;
                const pageSize = 1000;
                let hasMore = true;
                
                while (hasMore) {
                    const res = await getAllICT(pageNumber, pageSize);
                    const pageData = res.data?.data || res.data || {};
                    const data = pageData.content || pageData.data || pageData || [];
                    
                    // If no data returned, break to prevent infinite loop
                    if (!data || data.length === 0) {
                        break;
                    }
                    
                    allAssets = [...allAssets, ...data];
                    
                    // Check both camelCase and snake_case for pagination
                    const totalPages = pageData.totalPages || pageData.total_pages || 1;
                    
                    // If we got less data than pageSize, we're on the last page
                    if (data.length < pageSize) {
                        hasMore = false;
                    } else {
                        // Continue if there are more pages
                        hasMore = pageNumber < totalPages - 1;
                    }
                    
                    pageNumber++;
                }
            } else if (tabKey === "land") {
                // Handle land - use Land API
                let pageNumber = 0;
                const pageSize = 1000;
                let hasMore = true;
                
                while (hasMore) {
                    const res = await getAllLand(pageNumber, pageSize);
                    const pageData = res.data?.data || res.data || {};
                    const data = pageData.content || pageData.data || pageData || [];
                    
                    // If no data returned, break to prevent infinite loop
                    if (!data || data.length === 0) {
                        break;
                    }
                    
                    allAssets = [...allAssets, ...data];
                    
                    // Check both camelCase and snake_case for pagination
                    const totalPages = pageData.totalPages || pageData.total_pages || 1;
                    
                    // If we got less data than pageSize, we're on the last page
                    if (data.length < pageSize) {
                        hasMore = false;
                    } else {
                        // Continue if there are more pages
                        hasMore = pageNumber < totalPages - 1;
                    }
                    
                    pageNumber++;
                }
            } else if (tabKey === "machinery") {
                // Handle machinery - use Machinery API
                let pageNumber = 0;
                const pageSize = 1000;
                let hasMore = true;
                
                while (hasMore) {
                    const res = await getAllMachinery(pageNumber, pageSize);
                    const pageData = res.data?.data || res.data || {};
                    const data = pageData.content || pageData.data || pageData || [];
                    
                    // If no data returned, break to prevent infinite loop
                    if (!data || data.length === 0) {
                        break;
                    }
                    
                    allAssets = [...allAssets, ...data];
                    
                    // Check both camelCase and snake_case for pagination
                    const totalPages = pageData.totalPages || pageData.total_pages || 1;
                    
                    // If we got less data than pageSize, we're on the last page
                    if (data.length < pageSize) {
                        hasMore = false;
                    } else {
                        // Continue if there are more pages
                        hasMore = pageNumber < totalPages - 1;
                    }
                    
                    pageNumber++;
                }
            } else if (tabKey === "vehicles") {
                // Handle vehicles - use Vehicles API
                let pageNumber = 0;
                const pageSize = 1000;
                let hasMore = true;
                
                while (hasMore) {
                    const res = await getAllVehicles(pageNumber, pageSize);
                    const pageData = res.data?.data || res.data || {};
                    const data = pageData.content || pageData.data || pageData || [];
                    
                    // If no data returned, break to prevent infinite loop
                    if (!data || data.length === 0) {
                        break;
                    }
                    
                    allAssets = [...allAssets, ...data];
                    
                    // Check both camelCase and snake_case for pagination
                    const totalPages = pageData.totalPages || pageData.total_pages || 1;
                    
                    // If we got less data than pageSize, we're on the last page
                    if (data.length < pageSize) {
                        hasMore = false;
                    } else {
                        // Continue if there are more pages
                        hasMore = pageNumber < totalPages - 1;
                    }
                    
                    pageNumber++;
                }
            } else if (tabKey === "infrastructure") {
                // Handle infrastructure - use INFR category code API
                let pageNumber = 0;
                const pageSize = 1000;
                let hasMore = true;

                while (hasMore) {
                    const res = await getAllInfrastructure(pageNumber, pageSize);
                    const pageData = res.data?.data || res.data || {};
                    const data = pageData.content || pageData.data || pageData || [];

                    if (!data || data.length === 0) {
                        break;
                    }

                    allAssets = [...allAssets, ...data];

                    const totalPages = pageData.totalPages || pageData.total_pages || 1;

                    if (data.length < pageSize) {
                        hasMore = false;
                    } else {
                        hasMore = pageNumber < totalPages - 1;
                    }

                    pageNumber++;
                }
            }
            
            // Add reference_number and status aliases to all assets for all categories
            allAssets = allAssets.map(asset => {
                // Extract valuation object from various possible locations (same as buildings)
                const valuation = asset.asset_valuation || asset.assetValuation || asset.valuation || {};
                
                // Get valuation reference from various possible locations
                const valuationRef = 
                    valuation.valuation_ref || 
                    valuation.valuationRef || 
                    valuation.reference_number ||
                    asset.valuation_ref || 
                    asset.valuationRef || 
                    "";
                
                // Get valuation status from various possible locations
                const valuationStatus = 
                    valuation.valuation_status || 
                    valuation.valuationStatus || 
                    valuation.status ||
                    asset.valuation_status || 
                    asset.valuationStatus || 
                    "";
                
                return {
                    ...asset,
                    // Add aliases for easier identification in export
                    reference_number: valuationRef,
                    status: valuationStatus,
                };
            });
            
            return allAssets;
        } catch (err) {
            console.error(`Failed to fetch all assets for export (${tabKey}):`, err);
            console.error('Error details:', err.response?.data || err.message || err);
            return [];
        }
    }, []);

    // Open column selector modal - fetch all data first
    const openColumnSelector = async (exportType) => {
        setPendingExportType(exportType);
        setColumnModalOpen(true);
        setIsFetchingAllData(true);
        
        try {
            // Fetch all assets (ignoring pagination)
            const allAssets = await fetchAllAssetsForExport(activeTab);
            let assetsToUse = allAssets;
            
            if (allAssets.length === 0) {
                // If fetch returned empty, try using existing data from state
                const existingAssets = assetsByCategory[activeTab] || [];
                
                if (existingAssets.length === 0) {
                    setColumnModalOpen(false);
                    enqueueSnackbar(
                        `No ${activeTab} assets found to export. Please load data first.`,
                        {
                            variant: 'warning',
                            autoHideDuration: 4000,
                            anchorOrigin: {
                                vertical: 'top',
                                horizontal: 'right',
                            },
                        }
                    );
                    return;
                } else {
                    // Use existing data if fetch failed
                    enqueueSnackbar(
                        `Using currently loaded data for export (${existingAssets.length} items).`,
                        {
                            variant: 'info',
                            autoHideDuration: 3000,
                            anchorOrigin: {
                                vertical: 'top',
                                horizontal: 'right',
                            },
                        }
                    );
                    assetsToUse = existingAssets;
                }
            }
            
            // Store all assets temporarily for export
            setAssetsByCategory((prev) => ({
                ...prev,
                [`${activeTab}_export`]: assetsToUse
            }));
            
            // Generate headers dynamically from the data
            const headers = getExportHeaders(activeTab, assetsToUse);
            setExportHeaders(headers);
            
        } catch (error) {
            console.error("Error fetching all data for export:", error);
            
            // Try using existing data as fallback
            const existingAssets = assetsByCategory[activeTab] || [];
            if (existingAssets.length > 0) {
                enqueueSnackbar(
                    `Using currently loaded data for export (${existingAssets.length} items).`,
                    {
                        variant: 'info',
                        autoHideDuration: 3000,
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                    }
                );
                setAssetsByCategory((prev) => ({
                    ...prev,
                    [`${activeTab}_export`]: existingAssets
                }));
                
                // Generate headers from existing data
                const headers = getExportHeaders(activeTab, existingAssets);
                setExportHeaders(headers);
            } else {
                enqueueSnackbar(
                    'Failed to fetch data for export. Please check your connection and try again.',
                    {
                        variant: 'error',
                        autoHideDuration: 5000,
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                    }
                );
                setColumnModalOpen(false);
            }
        } finally {
            setIsFetchingAllData(false);
        }
    };

    // Perform export with selected columns
    const performExport = async () => {
        try {
            // Use export-specific data if available, otherwise fall back to current tab data
            const assets = assetsByCategory[`${activeTab}_export`] || assetsByCategory[activeTab] || [];
            // Use stored headers or generate from data
            const allHeaders = exportHeaders.length > 0 
                ? exportHeaders 
                : getExportHeaders(activeTab, assets);

            // Read selected columns from DOM checkboxes
            const checkboxNodes = document.querySelectorAll(
                'input[name="export-column"]'
            );
            const selectedKeys = [];
            checkboxNodes.forEach((node, index) => {
                if (node.checked) {
                    const header = allHeaders[index];
                    if (header) {
                        selectedKeys.push(header.key);
                    }
                }
            });

            // Filter headers based on selected keys, maintaining order and ensuring serialNumber, building_serial_no/site_work_serial_no, and reference_number are first
            const headers = allHeaders
                .filter((h) => selectedKeys.includes(h.key))
                .sort((a, b) => {
                    // Put serialNumber first - always
                    if (a.key === "serialNumber") return -1;
                    if (b.key === "serialNumber") return 1;
                    // Put building_serial_no second (for buildings) - always
                    if (a.key === "building_serial_no") return -1;
                    if (b.key === "building_serial_no") return 1;
                    // Put site_work_serial_no second (for siteworks) - always
                    if (a.key === "site_work_serial_no") return -1;
                    if (b.key === "site_work_serial_no") return 1;
                    // Put reference_number third - always
                    if (a.key === "reference_number") return -1;
                    if (b.key === "reference_number") return 1;
                    // Maintain original order for other headers
                    const aIndex = allHeaders.findIndex(h => h.key === a.key);
                    const bIndex = allHeaders.findIndex(h => h.key === b.key);
                    return aIndex - bIndex;
                });

            if (headers.length === 0) {
                enqueueSnackbar(
                    'Please select at least one column to export.',
                    {
                        variant: 'info',
                        autoHideDuration: 4000,
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                    }
                );
                return;
            }


            // Process assets data - format special fields like "utilities" and "category_name"
            // Add serial numbers like in the list (1-based indexing)
            const processedAssets = assets.map((asset, index) => {
                const processed = { ...asset };
                
                // Ensure photo_urls is included if it exists in the asset
                if (asset.photo_urls !== undefined) {
                    processed.photo_urls = asset.photo_urls;
                }
                
                // Add serial number (like in the list view) - always add it
                processed.serialNumber = index + 1;
                
                // Format utilities column (combine has_water and has_electricity)
                if (selectedKeys.includes('utilities')) {
                    const utilities = [];
                    if (asset.has_water) utilities.push('Water');
                    if (asset.has_electricity) utilities.push('Electricity');
                    processed.utilities = utilities.length > 0 ? utilities.join(', ') : '-';
                }
                
                // Format category_name from asset_category
                if (selectedKeys.includes('category_name')) {
                    processed.category_name = asset.asset_category?.name || asset.category_name || '-';
                }
                
                // Ensure description is included (might come from nested objects for buildings)
                if (selectedKeys.includes('description')) {
                    processed.description = asset.description || asset.building?.description || '-';
                }
                
                // Format building_use (might be an object)
                if (selectedKeys.includes('building_use')) {
                    processed.building_use = asset.building_use?.name || asset.building_use || '-';
                }
                
                // Format ward (might be an object)
                if (selectedKeys.includes('ward')) {
                    processed.ward = asset.ward?.name || asset.ward || '-';
                }

                // Attachment URL fields (only add if field exists in data to avoid unnecessary processing)
                if (selectedKeys.includes('barcode_image_url')) {
                    // Only process if field exists in asset object
                    if ('barcode_image_url' in asset || 'barcodeImageUrl' in asset) {
                        processed.barcode_image_url = asset.barcode_image_url || asset.barcodeImageUrl || '-';
                    }
                }

                if (selectedKeys.includes('sketch_image_url')) {
                    // Only process if field exists in asset object (buildings only)
                    if ('sketch_image_url' in asset || 'sketchImageUrl' in asset) {
                        processed.sketch_image_url = asset.sketch_image_url || asset.sketchImageUrl || '-';
                    }
                }

                // Format valuer_name from related valuation (if present)
                if (selectedKeys.includes('valuer_name')) {
                    const valuation = asset.asset_valuation || asset.assetValuation || asset.valuation;
                    const valuer = valuation?.valuer_user || valuation?.valuerUser;
                    const valuerName =
                        asset.valuer_name || asset.valuerName ||
                        (valuer
                            ? [
                                  valuer.first_name || valuer.firstName || "",
                                  valuer.middle_name || valuer.middleName || "",
                                  valuer.last_name || valuer.lastName || "",
                              ]
                                  .filter(Boolean)
                                  .join(" ")
                            : "-");
                    processed.valuer_name = valuerName;
                }
                
                // Format boolean fields
                if (selectedKeys.includes('has_water')) {
                    processed.has_water = asset.has_water ? 'Yes' : 'No';
                }
                if (selectedKeys.includes('has_electricity')) {
                    processed.has_electricity = asset.has_electricity ? 'Yes' : 'No';
                }
                if (selectedKeys.includes('is_land_surveyed')) {
                    processed.is_land_surveyed = asset.is_land_surveyed ? 'Yes' : 'No';
                }
                
                // Format date fields
                if (selectedKeys.includes('acquisition_date') && asset.acquisition_date) {
                    try {
                        processed.acquisition_date = new Date(asset.acquisition_date).toLocaleDateString();
                    } catch (e) {
                        processed.acquisition_date = asset.acquisition_date;
                    }
                }
                if (selectedKeys.includes('right_of_occupance_start') && asset.right_of_occupance_start) {
                    try {
                        processed.right_of_occupance_start = new Date(asset.right_of_occupance_start).toLocaleDateString();
                    } catch (e) {
                        processed.right_of_occupance_start = asset.right_of_occupance_start;
                    }
                }
                if (selectedKeys.includes('right_of_occupance_end') && asset.right_of_occupance_end) {
                    try {
                        processed.right_of_occupance_end = new Date(asset.right_of_occupance_end).toLocaleDateString();
                    } catch (e) {
                        processed.right_of_occupance_end = asset.right_of_occupance_end;
                    }
                }
                if (selectedKeys.includes('created_at') && asset.created_at) {
                    try {
                        processed.created_at = new Date(asset.created_at).toLocaleString();
                    } catch (e) {
                        processed.created_at = asset.created_at;
                    }
                }
                if (selectedKeys.includes('updated_at') && asset.updated_at) {
                    try {
                        processed.updated_at = new Date(asset.updated_at).toLocaleString();
                    } catch (e) {
                        processed.updated_at = asset.updated_at;
                    }
                }
                
                // Format numeric fields with proper formatting
                if (selectedKeys.includes('plot_size') && asset.plot_size) {
                    processed.plot_size = typeof asset.plot_size === 'number' 
                        ? asset.plot_size.toLocaleString() 
                        : asset.plot_size;
                }
                if (selectedKeys.includes('rate_per_unit') && asset.rate_per_unit) {
                    processed.rate_per_unit = typeof asset.rate_per_unit === 'number' 
                        ? asset.rate_per_unit.toLocaleString() 
                        : asset.rate_per_unit;
                }
                if (selectedKeys.includes('latitude') && asset.latitude) {
                    processed.latitude = typeof asset.latitude === 'number' 
                        ? asset.latitude.toString() 
                        : asset.latitude;
                }
                if (selectedKeys.includes('longitude') && asset.longitude) {
                    processed.longitude = typeof asset.longitude === 'number' 
                        ? asset.longitude.toString() 
                        : asset.longitude;
                }
                
                // Handle arrays - stringify them for export
                selectedKeys.forEach(key => {
                    if (Array.isArray(processed[key])) {
                        processed[key] = processed[key].join(', ');
                    }
                });
                
                // Explicitly handle photo_urls for site works - ensure it's formatted as a string
                if (selectedKeys.includes('photo_urls')) {
                    if (Array.isArray(asset.photo_urls) && asset.photo_urls.length > 0) {
                        processed.photo_urls = asset.photo_urls.join(', ');
                    } else if (asset.photo_urls) {
                        processed.photo_urls = Array.isArray(asset.photo_urls) 
                            ? asset.photo_urls.join(', ') 
                            : String(asset.photo_urls);
                    } else {
                        processed.photo_urls = '-';
                    }
                }
                
                return processed;
            });

            const filename = `${activeTab}_assets_${new Date().toISOString().split("T")[0]}`;
            
            if (pendingExportType === 'csv') {
                exportToCSV(processedAssets, headers, `${filename}.csv`);
            } else if (pendingExportType === 'excel') {
                exportToExcel(processedAssets, headers, `${filename}.xlsx`);
            } else if (pendingExportType === 'pdf') {
                // Get current logged-in user info
                let exportedBy = "System User";
                try {
                    const authUser = localStorage.getItem("authUser");
                    if (authUser) {
                        const { user } = JSON.parse(authUser);
                        if (user) {
                            const firstName = user.first_name || "";
                            const lastName = user.last_name || "";
                            exportedBy = `${firstName} ${lastName}`.trim() || user.username || user.email || "System User";
                        }
                    }
                } catch (error) {
                    console.warn("Could not get user info:", error);
                }

                // Extract client and project names from assets (if available)
                let clientName = null;
                let projectName = null;
                
                if (assets.length > 0) {
                    // Try to get from first asset's valuation
                    const firstAsset = assets[0];
                    if (firstAsset.valuation) {
                        if (firstAsset.valuation.client) {
                            clientName = firstAsset.valuation.client.name || firstAsset.valuation.client_name || null;
                        }
                        if (firstAsset.valuation.project) {
                            projectName = firstAsset.valuation.project.name || firstAsset.valuation.project_name || null;
                        }
                    }
                    // Fallback: check if asset has direct client/project fields
                    if (!clientName && firstAsset.client_name) {
                        clientName = firstAsset.client_name;
                    }
                    if (!projectName && firstAsset.project_name) {
                        projectName = firstAsset.project_name;
                    }
                }

                const title = `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Assets Report`;
                const metadata = {
                    logoPath: logo,
                    exportedBy: exportedBy,
                    clientName: clientName,
                    projectName: projectName,
                };
                
                exportToPDF(processedAssets, headers, `${filename}.pdf`, title, metadata);
            }
            
            // Clean up export-specific data
            setAssetsByCategory((prev) => {
                const updated = { ...prev };
                delete updated[`${activeTab}_export`];
                return updated;
            });
            
            setColumnModalOpen(false);
        } catch (error) {
            console.error("Export failed:", error);
            enqueueSnackbar(
                'Export failed. Please try again.',
                {
                    variant: 'error',
                    autoHideDuration: 4000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
        }
    };

    // Select/Deselect all columns
    const toggleAllColumns = () => {
        const checkboxNodes = document.querySelectorAll(
            'input[name="export-column"]'
        );

        // Determine if all are selected based on DOM state
        const allSelected =
            checkboxNodes.length > 0 &&
            Array.from(checkboxNodes).every((node) => node.checked);

        // Apply new state directly to DOM checkboxes
        Array.from(checkboxNodes).forEach((node) => {
            // eslint-disable-next-line no-param-reassign
            node.checked = !allSelected;
        });
    };

    // Fetch all valuations with their assets for export
    const fetchValuationsWithAssets = useCallback(async () => {
        try {
            let allValuationsWithAssets = [];
            let pageNumber = 0;
            const pageSize = 100; // Fetch valuations in batches
            let hasMore = true;
            
            while (hasMore) {
                const res = await getAllAssetValuations({ page: pageNumber, size: pageSize });
                const pageData = res.data?.data || res.data || {};
                const valuations = pageData.content || pageData.data || [];
                
                // If no data returned, break to prevent infinite loop
                if (!valuations || valuations.length === 0) {
                    break;
                }
                
                // For each valuation, fetch its assets
                for (const valuation of valuations) {
                    try {
                        const assetsRes = await getAssetsByValuation(valuation.id);
                        const assets = assetsRes.data?.data || assetsRes.data || [];
                        
                        // Create rows: each asset with valuation details
                        const rows = assets.map(asset => ({
                            // Valuation details (repeated for each asset)
                            valuation_id: valuation.id,
                            valuation_number: valuation.valuation_number || "",
                            client_name: valuation.client?.name || valuation.client_name || "",
                            project_name: valuation.project?.name || valuation.project_name || "",
                            valuation_date: valuation.valuation_date || "",
                            status: valuation.status || "",
                            created_at: valuation.created_at || "",
                            created_by: valuation.created_by || "",
                            
                            // Asset details
                            ...asset,
                        }));
                        
                        allValuationsWithAssets = [...allValuationsWithAssets, ...rows];
                    } catch (err) {
                        console.error(`Failed to fetch assets for valuation ${valuation.id}:`, err);
                        // Continue with next valuation even if one fails
                    }
                }
                
                // Check both camelCase and snake_case for pagination
                const totalPages = pageData.totalPages || pageData.total_pages || 1;
                
                // If we got less data than pageSize, we're on the last page
                if (valuations.length < pageSize) {
                    hasMore = false;
                } else {
                    // Continue if there are more pages
                    hasMore = pageNumber < totalPages - 1;
                }
                
                pageNumber++;
            }
            
            return allValuationsWithAssets;
        } catch (err) {
            console.error("Failed to fetch valuations with assets:", err);
            return [];
        }
    }, []);

    // Export by Valuation handler
    const handleExportByValuation = async (exportType) => {
        setIsFetchingAllData(true);
        setPendingExportType(exportType);
        
        try {
            enqueueSnackbar(
                'Fetching valuations and assets... This may take a while.',
                {
                    variant: 'info',
                    autoHideDuration: 3000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
            
            const valuationsWithAssets = await fetchValuationsWithAssets();
            
            if (valuationsWithAssets.length === 0) {
                enqueueSnackbar(
                    'No valuations with assets found to export.',
                    {
                        variant: 'warning',
                        autoHideDuration: 4000,
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                    }
                );
                setIsFetchingAllData(false);
                return;
            }
            
            // Prepare headers for export (valuation fields + asset fields)
            const valuationHeaders = [
                { key: "valuation_id", label: "Valuation ID" },
                { key: "valuation_number", label: "Valuation Number" },
                { key: "client_name", label: "Client Name" },
                { key: "project_name", label: "Project Name" },
                { key: "valuation_date", label: "Valuation Date" },
                { key: "status", label: "Status" },
            ];
            
            // Get asset headers dynamically from the data
            const assetHeaders = getExportHeaders("all", valuationsWithAssets);
            
            // Combine headers (valuation first, then assets)
            const allHeaders = [...valuationHeaders, ...assetHeaders];
            
            // Process data for export
            const processedData = valuationsWithAssets.map((row, index) => {
                const processed = { ...row };
                
                // Format utilities
                if (row.has_water !== undefined && row.has_electricity !== undefined) {
                    const water = row.has_water ? "Water" : "";
                    const electricity = row.has_electricity ? "Electricity" : "";
                    processed.utilities = [water, electricity].filter(Boolean).join(", ") || "None";
                }
                
                processed.name = resolveAssetNameDisplay(row);
                processed.building_name = resolveBuildingNameDisplay(row, processed.building_name || "-");
                
                return processed;
            });
            
            // Perform export
            const filename = `valuations_with_assets_${new Date().toISOString().split('T')[0]}`;
            
            if (exportType === 'csv') {
                exportToCSV(processedData, allHeaders, `${filename}.csv`);
            } else if (exportType === 'excel') {
                exportToExcel(processedData, allHeaders, `${filename}.xlsx`);
            } else if (exportType === 'pdf') {
                // Get current logged-in user info
                let exportedBy = "System User";
                try {
                    const authUser = localStorage.getItem("authUser");
                    if (authUser) {
                        const user = JSON.parse(authUser);
                        if (user) {
                            const firstName = user.first_name || user.firstName || "";
                            const lastName = user.last_name || user.lastName || "";
                            exportedBy = `${firstName} ${lastName}`.trim() || user.username || user.email || "System User";
                        }
                    }
                } catch (error) {
                    console.error("Error getting user info:", error);
                }
                
                const title = "Valuations with Assets Report";
                const metadata = {
                    exportedBy: exportedBy,
                    clientName: "", // Multiple clients in this export
                    projectName: "", // Multiple projects in this export
                };
                
                exportToPDF(processedData, allHeaders, `${filename}.pdf`, title, metadata);
            }
            
            enqueueSnackbar(
                `Successfully exported ${valuationsWithAssets.length} records.`,
                {
                    variant: 'success',
                    autoHideDuration: 4000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
        } catch (error) {
            console.error("Error exporting by valuation:", error);
            enqueueSnackbar(
                'Failed to export valuations with assets. Please try again.',
                {
                    variant: 'error',
                    autoHideDuration: 5000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
        } finally {
            setIsFetchingAllData(false);
        }
    };

    // Export handlers (now open column selector)
    const handleExportCSV = () => openColumnSelector('csv');
    const handleExportExcel = () => openColumnSelector('excel');

    // Export to PDF (now opens column selector)
    const handleExportPDF = () => openColumnSelector('pdf');

    const getBarcodePdfsForCategory = useCallback((files, tabKey) => {
        if (!Array.isArray(files)) return [];

        const prefix = BARCODE_FILE_PREFIX_BY_TAB[tabKey];
        if (!prefix) return [];

        return files
            .filter((file) =>
                typeof file?.filename === "string" &&
                file.filename.toLowerCase().startsWith(`barcodes-${prefix}`)
            )
            .map((file) => ({
                ...file,
                downloadUrl: resolveServerFileUrl(file.url),
            }));
    }, []);

    const fetchCategoryBarcodePdfs = useCallback(async (tabKey) => {
        setLoadingBarcodePdfs(true);
        try {
            const response = await getBarcodePdfFiles();
            const barcodeFiles = response?.data?.data?.["barcode-pdfs"] || [];
            const categoryFiles = getBarcodePdfsForCategory(barcodeFiles, tabKey);

            setBarcodePdfsByCategory((prev) => ({
                ...prev,
                [tabKey]: categoryFiles,
            }));
        } catch (error) {
            console.error("Failed to fetch barcode PDF files:", error);
            enqueueSnackbar("Failed to fetch barcode PDF files.", {
                variant: "error",
                autoHideDuration: 4000,
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                },
            });
            setBarcodePdfsByCategory((prev) => ({
                ...prev,
                [tabKey]: [],
            }));
        } finally {
            setLoadingBarcodePdfs(false);
        }
    }, [getBarcodePdfsForCategory]);

    const handleOpenBarcodePdfModal = async () => {
        if (activeTab === "all") return;

        setBarcodePdfModalOpen(true);

        if (barcodePdfsByCategory[activeTab] !== undefined) {
            return;
        }

        await fetchCategoryBarcodePdfs(activeTab);
    };

    const activeCategoryBarcodePdfs = barcodePdfsByCategory[activeTab] || [];

    // Update active tab when URL param changes
    useEffect(() => {
        if (categoryParam && TAB_KEYS.includes(categoryParam)) {
            setActiveTab(categoryParam);
        }
    }, [categoryParam]);

    // Fetch assets by category code with pagination (server-side) - supports append mode for infinite scroll
    const fetchAssetsByCategory = useCallback(async (tabKey, pageNumber = 0, pageSize = DEFAULT_PAGE_SIZE, append = false) => {
        if (!(tabKey in CATEGORY_CODE_MAP)) return;
        
        // Use different loading states for initial load vs loading more
        if (append) {
            setLoadingMoreByCategory((prev) => ({ ...prev, [tabKey]: true }));
        } else {
            setLoadingByCategory((prev) => ({ ...prev, [tabKey]: true }));
        }
        
        try {
            let allAssets = [];
            let totalElements = 0;
            
            // Handle "all" category - fetch all assets with pagination
            if (tabKey === "all") {
                const res = await getAllAssets(pageNumber, pageSize);
                const pageData = res.data?.data || res.data || {};
                // Spring Data Page object has 'content' array, or direct array
                allAssets = pageData.content || pageData.data || pageData || [];
                const pagination = res.data?.pagination || {};
                // IMPORTANT: API returns snake_case (total_elements, total_pages) not camelCase
                // Support both formats for compatibility
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : (pagination.totalElements !== undefined && pagination.totalElements !== null
                            ? pagination.totalElements
                            : (pagination.total_elements !== undefined && pagination.total_elements !== null
                                ? pagination.total_elements
                                : 0)));
                
                // Calculate total pages and hasMore for pagination
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (pagination.totalPages !== undefined && pagination.totalPages !== null
                            ? pagination.totalPages
                            : (pagination.total_pages !== undefined && pagination.total_pages !== null
                                ? pagination.total_pages
                                : Math.ceil(totalElements / pageSize))));
                
                // Update pagination info for "all" category
                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: pageNumber,
                        pageSize: pageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: pageNumber < totalPages - 1, // Check if there are more pages
                    },
                }));
            } else if (tabKey === "siteworks") {
                let siteWorks = [];
                if (assetIdParam || valuationIdParam) {
                    const res = await getSiteWorks(pageNumber, pageSize, assetIdParam || null, valuationIdParam || null);
                    const payload = res.data?.data || res.data || {};
                    siteWorks = payload.content || payload.data || [];
                    totalElements = payload.totalElements ?? payload.total_elements ?? siteWorks.length;
                    const totalPages = payload.totalPages ?? payload.total_pages ?? Math.max(1, Math.ceil(totalElements / pageSize));
                    setPaginationByTab((prev) => ({
                        ...prev,
                        [tabKey]: {
                            pageNumber,
                            pageSize,
                            totalElements,
                            totalPages,
                            hasMore: pageNumber < totalPages - 1,
                        },
                    }));
                } else {
                    const res = await getAllSiteWorksWithDetails();
                    siteWorks = res.data?.data || res.data || [];
                }
                
                // Transform SiteWorkQueryResultDTO to match AssetDTO structure for display
                // The new query returns flat structure with all fields already populated
                // Sort by asset_id and site_work_serial_no to ensure proper grouping
                const sortedSiteWorks = [...siteWorks].sort((a, b) => {
                    const assetIdA = a.asset_id || a.assetId || a.asset?.id || "";
                    const assetIdB = b.asset_id || b.assetId || b.asset?.id || "";
                    if (assetIdA !== assetIdB) {
                        return assetIdA.localeCompare(assetIdB);
                    }
                    const serialA = a.site_work_serial_no || a.siteWorkSerialNo || 0;
                    const serialB = b.site_work_serial_no || b.siteWorkSerialNo || 0;
                    return serialA - serialB;
                });
                
                allAssets = sortedSiteWorks.map(sw => {
                    // Start with all original fields from the query (ensures all fields are included)
                    const result = {
                        ...sw,
                        
                        // Add computed/aliased fields for compatibility
                        id: sw.id || null,
                        asset_id: sw.asset_id || sw.assetId || sw.asset?.id || null,
                        name: sw.site_work_type || sw.asset_name || sw.asset?.name || "-",
                        asset_name: sw.asset_name || sw.asset?.name || "",
                        asset_serial_no: sw.site_work_serial_no || sw.siteWorkSerialNo || null,
                        site_work_type: sw.site_work_type || "",
                        other_name: sw.other_specify || sw.otherName || "",
                        other_specify: sw.other_specify || "",
                        category_name: sw.asset_category || sw.asset?.asset_category?.name || "Site Works",
                        category_code: "SITE_WORKS",
                        ward: sw.ward || sw.asset?.ward?.name || "",
                        ward_name: sw.ward || sw.asset?.ward?.name || "",
                        location: sw.location || sw.ward || sw.asset?.institution_name || sw.asset?.institutionName || "-",
                        plot_size: sw.plot_area || null, // Alias for compatibility
                        plot_area: sw.plot_area || null,
                        quantity: 1, // Default for site works
                        description: sw.description || sw.asset_description || "",
                        asset_description: sw.asset_description || "",
                        
                        // Site work serial number (from SQL query - already calculated correctly)
                        site_work_serial_no: sw.site_work_serial_no || sw.siteWorkSerialNo || null,
                        
                        // Additional fields for export compatibility
                        acquisition_date: sw.acquisition_date || sw.asset?.acquisition_date || sw.asset?.acquisitionDate || null,
                        useful_life_span_years: sw.useful_life_span_years || sw.asset?.useful_life_span_years || null,
                        make: sw.make || sw.asset?.make || "",
                        model: sw.model || sw.asset?.model || "",
                        registration_number: sw.registration_number || sw.asset?.registration_number || "",
                        year_of_manufacture: sw.year_of_manufacture || sw.asset?.year_of_manufacture || null,
                        engine_capacity: sw.engine_capacity || sw.asset?.engine_capacity || null,
                        fuel_type: sw.fuel_type || sw.asset?.fuel_type || "",
                        body_type: sw.body_type || sw.asset?.body_type || "",
                        body_type_others: sw.body_type_others || sw.asset?.body_type_others || "",
                        
                        // Ensure photo_urls is always an array
                        photo_urls: Array.isArray(sw.photo_urls) 
                            ? sw.photo_urls 
                            : (sw.photo_urls ? [sw.photo_urls] : []),
                        
                        // Combine remarks
                        remark: sw.site_work_remark || sw.asset_remark || sw.remark || "",
                        site_work_remark: sw.site_work_remark || sw.remark || "",
                        asset_remark: sw.asset_remark || sw.asset?.remark || "",
                        
                        // Add aliases for reference number and status for easier identification
                        reference_number: sw.reference_number || sw.valuation_ref || sw.asset?.valuation_ref || "",
                        status: sw.valuation_status || sw.asset?.valuation_status || "",
                        valuation_ref: sw.valuation_ref || sw.asset?.valuation_ref || "",
                        valuation_status: sw.valuation_status || sw.asset?.valuation_status || "",
                        valuer_name: sw.valuer_name || sw.asset?.valuer_name || sw.asset?.valuerName || "",
                        
                        // Observed condition
                        observed_condition: sw.observed_condition || "",
                        observed_condition_name: sw.observed_condition || "",
                        
                        // Institution fields
                        institution_name: sw.institution_name || sw.asset?.institution_name || sw.asset?.institutionName || "",
                        institution_category: sw.institution_category || sw.asset?.institution_category || sw.asset?.institutionCategory || "",
                        
                        // Utilities
                        has_water: sw.has_water || sw.asset?.has_water || false,
                        has_electricity: sw.has_electricity || sw.asset?.has_electricity || false,
                        
                        // Dimensions and serial number
                        dimensions: sw.dimensions || "",
                        serial_number: sw.serial_number || "",
                    };
                    
                    return result;
                });
                
                if (!(assetIdParam || valuationIdParam)) {
                    totalElements = siteWorks.length;
                    const totalPages = 1;
                    const currentPage = 0;
                    const actualPageSize = totalElements;

                    setPaginationByTab((prev) => ({
                        ...prev,
                        [tabKey]: {
                            pageNumber: currentPage,
                            pageSize: actualPageSize,
                            totalElements,
                            totalPages,
                            hasMore: false,
                        },
                    }));
                }
            } else if (tabKey === "buildings") {
                // Handle buildings - use Buildings API
                const res = await getAllBuildings(pageNumber, pageSize);
                // Response structure: { status: "success", data: { content: [...], totalElements, totalPages, ... } }
                const pageData = res.data?.data || {};
                // Spring Data Page object has 'content' array
                const buildings = pageData.content || pageData.data || [];
                
                // Calculate building_serial_no for each building (group by asset.id, sort by is_primary DESC, created_at, id)
                // This matches the SQL query: ROW_NUMBER() OVER (PARTITION BY a.id ...)
                const buildingsByAsset = {};
                buildings.forEach(building => {
                    // Use asset.id (same as SQL query uses a.id)
                    const assetId = building.asset?.id || building.asset_id;
                    if (!assetId) return;
                    
                    if (!buildingsByAsset[assetId]) {
                        buildingsByAsset[assetId] = [];
                    }
                    buildingsByAsset[assetId].push(building);
                });
                
                // Sort buildings within each asset group and assign serial numbers (matching SQL query order)
                Object.keys(buildingsByAsset).forEach(assetId => {
                    buildingsByAsset[assetId].sort((a, b) => {
                        // Primary first (is_primary DESC)
                        const aPrimary = a.is_primary || a.isPrimary || false;
                        const bPrimary = b.is_primary || b.isPrimary || false;
                        if (aPrimary && !bPrimary) return -1;
                        if (!aPrimary && bPrimary) return 1;
                        // Then by created_at
                        const aCreated = a.created_at || a.createdAt;
                        const bCreated = b.created_at || b.createdAt;
                        if (aCreated && bCreated) {
                            return new Date(aCreated) - new Date(bCreated);
                        }
                        // Finally by id
                        return (a.id || "").localeCompare(b.id || "");
                    });
                    
                    // Assign serial numbers starting from 1 for each asset group
                    buildingsByAsset[assetId].forEach((building, index) => {
                        building.building_serial_no = index + 1;
                    });
                });
                
                // Transform BuildingDTO to match AssetDTO structure for display
                // Buildings API returns AssetBuildingDTO which has asset relationship
                allAssets = buildings.map(building => {
                    const asset = building.asset || {};
                    const valuation = building.asset_valuation || building.assetValuation || asset.asset_valuation || asset.assetValuation || asset.valuation || {};
                    const assetCategory = asset.asset_category || {};
                    const ward = asset.ward || {};
                    const buildingUse = asset.building_use || {};
                    const valuer = valuation.valuer_user || valuation.valuerUser;
                    const valuerName =
                        asset.valuer_name || asset.valuerName ||
                        (valuer
                            ? [
                                  valuer.first_name || valuer.firstName || "",
                                  valuer.middle_name || valuer.middleName || "",
                                  valuer.last_name || valuer.lastName || "",
                              ]
                                  .filter(Boolean)
                                  .join(" ")
                            : undefined);
                    const resolvedBuildingName = resolveBuildingNameDisplay({
                        building_name: building.building_name || asset.building_name || "",
                        buildingName: building.buildingName || asset.buildingName || "",
                        other_building_name: building.other_building_name || asset.other_building_name || "",
                        otherBuildingName: building.otherBuildingName || asset.otherBuildingName || "",
                    }, "");
                    
                    return {
                        // Building fields
                        id: building.id || asset.id,
                        asset_id: building.asset_id || asset.id,
                        valuation_id: building.valuation_id || asset.asset_valuation_id,
                        
                        // Building serial number (calculate based on order - will be set properly when grouped by asset)
                        building_serial_no: building.building_serial_no || building.serial_number || null,
                        
                        // Asset basic fields
                        name: asset.name || resolvedBuildingName || building.building_name || "-",
                        other_name: asset.other_name || building.other_building_name || "",
                        building_name: resolvedBuildingName || building.building_name || asset.building_name || "",
                        other_building_name: building.other_building_name || asset.other_building_name || "",
                        description: asset.description || building.description || "",
                        quantity: asset.quantity || 1,
                        latitude: asset.latitude || null,
                        longitude: asset.longitude || null,
                        location: asset.location || "-",
                        serial_number: asset.serial_number || "",
                        observed_condition: asset.observed_condition || "",
                        observed_condition_name: asset.observed_condition_name || asset.observed_condition || "",
                        remark: asset.remark || building.remark || "",
                        
                        // Valuer / valuation context
                        valuer_name: valuerName,
                        client_name: asset.client_name || valuation.client_name || valuation.client?.name || "",
                        project_name: asset.project_name || valuation.project_name || valuation.project?.name || "",
                        
                        // Valuation reference and status (check multiple possible locations)
                        valuation_ref: valuation.valuation_ref || 
                                     valuation.valuationRef || 
                                     valuation.reference_number ||
                                     asset.valuation_ref ||
                                     asset.valuationRef ||
                                     building.valuation_ref ||
                                     building.valuationRef ||
                                     "",
                        valuation_status: valuation.valuation_status || 
                                        valuation.valuationStatus || 
                                        valuation.status ||
                                        asset.valuation_status ||
                                        asset.valuationStatus ||
                                        building.valuation_status ||
                                        building.valuationStatus ||
                                        "",
                        reference_number: valuation.valuation_ref || 
                                       valuation.valuationRef || 
                                       valuation.reference_number ||
                                       asset.valuation_ref ||
                                       asset.valuationRef ||
                                       building.valuation_ref ||
                                       building.valuationRef ||
                                       "",
                        status: valuation.valuation_status || 
                               valuation.valuationStatus || 
                               valuation.status ||
                               asset.valuation_status ||
                               asset.valuationStatus ||
                               building.valuation_status ||
                               building.valuationStatus ||
                               "",
                        
                        // Asset category
                        asset_category_id: asset.asset_category_id,
                        category_name: assetCategory.name || "",
                        category_code: assetCategory.code || "",
                        
                        // Ward and location details
                        ward_id: asset.ward_id,
                        ward_name: ward.name || "",
                        council_name: ward.council?.name || "",
                        district_name: ward.council?.district?.name || "",
                        region_name: ward.council?.district?.region?.name || "",
                        
                        // Institution
                        institution_name: asset.institution_name || "",
                        institution_category: asset.institution_category || "",
                        
                        // Building details
                        building_number: asset.building_number || "",
                        plot_number: asset.plot_number || "",
                        block_number: asset.block_number || "",
                        certificate_of_title: asset.certificate_of_title || "",
                        size_unit: asset.size_unit || "",
                        number_of_buildings: asset.number_of_buildings || null,
                        building_use_id: asset.building_use_id,
                        building_use_name: buildingUse.name || "",
                        building_use_code: buildingUse.code || "",
                        
                        // Utilities
                        has_water: asset.has_water || false,
                        has_electricity: asset.has_electricity || false,
                        
                        // Financial fields
                        current_replacement_cost: asset.current_replacement_cost || 0,
                        historical_cost: asset.historical_cost || 0,
                        accumulated_depreciation_amount: asset.accumulated_depreciation_amount || 0,
                        fair_value: asset.fair_value || 0,
                        net_book_value: asset.net_book_value || 0,
                        remaining_useful_life_years: asset.remaining_useful_life_years || 0,
                        
                        // Valuation details
                        asset_valuation_id: asset.asset_valuation_id,
                        
                        // Dates
                        acquisition_date: asset.acquisition_date || "",
                        useful_life_span_years: asset.useful_life_span_years || 0,
                        
                        // Other
                        is_land_surveyed: asset.is_land_surveyed || false,
                        
                        // Include all other asset and building fields for compatibility
                        ...asset,
                        ...building,
                    };
                });
                
                // Sort allAssets by asset_id and building_serial_no to maintain grouping
                allAssets.sort((a, b) => {
                    // First sort by asset_id
                    const aAssetId = a.asset_id || a.id || "";
                    const bAssetId = b.asset_id || b.id || "";
                    if (aAssetId !== bAssetId) {
                        return aAssetId.localeCompare(bAssetId);
                    }
                    // Within same asset, sort by building_serial_no
                    const aSerial = a.building_serial_no || 0;
                    const bSerial = b.building_serial_no || 0;
                    return aSerial - bSerial;
                });
                
                // Get pagination info from Spring Data Page object
                // IMPORTANT: API returns snake_case (total_elements, total_pages) not camelCase
                // Support both formats for compatibility
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : 0);
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (totalElements > 0 ? Math.ceil(totalElements / pageSize) : 1));
                const currentPage = pageData.number !== undefined ? pageData.number : (pageData.page !== undefined ? pageData.page : pageNumber);
                const actualPageSize = pageData.size !== undefined ? pageData.size : pageSize;
                
                // Calculate hasMore: true if there are more pages to load
                const hasMorePages = totalElements > 0 && currentPage < totalPages - 1;
                
                // Update pagination info for Buildings
                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: currentPage,
                        pageSize: actualPageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: hasMorePages,
                    },
                }));
            } else if (tabKey === "equipment") {
                // Handle equipment - use Equipment API
                const res = await getAllEquipments(pageNumber, pageSize);
                const pageData = res.data?.data || res.data || {};
                allAssets = pageData.content || pageData.data || pageData || [];
                const pagination = res.data?.pagination || {};
                // IMPORTANT: API returns snake_case (total_elements, total_pages) not camelCase
                // Support both formats for compatibility
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : (pagination.totalElements !== undefined && pagination.totalElements !== null
                            ? pagination.totalElements
                            : (pagination.total_elements !== undefined && pagination.total_elements !== null
                                ? pagination.total_elements
                                : 0)));
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (totalElements > 0 ? Math.ceil(totalElements / pageSize) : 1));
                const currentPage = pageData.number !== undefined ? pageData.number : (pageData.page !== undefined ? pageData.page : pageNumber);
                const actualPageSize = pageData.size !== undefined ? pageData.size : pageSize;
                const hasMorePages = totalElements > 0 && currentPage < totalPages - 1;
                
                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: currentPage,
                        pageSize: actualPageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: hasMorePages,
                    },
                }));
            } else if (tabKey === "furniture") {
                // Handle furniture - use Furniture API
                const res = await getAllFurniture(pageNumber, pageSize);
                const pageData = res.data?.data || res.data || {};
                allAssets = pageData.content || pageData.data || pageData || [];
                const pagination = res.data?.pagination || {};
                // IMPORTANT: API returns snake_case (total_elements, total_pages) not camelCase
                // Support both formats for compatibility
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : (pagination.totalElements !== undefined && pagination.totalElements !== null
                            ? pagination.totalElements
                            : (pagination.total_elements !== undefined && pagination.total_elements !== null
                                ? pagination.total_elements
                                : 0)));
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (totalElements > 0 ? Math.ceil(totalElements / pageSize) : 1));
                const currentPage = pageData.number !== undefined ? pageData.number : (pageData.page !== undefined ? pageData.page : pageNumber);
                const actualPageSize = pageData.size !== undefined ? pageData.size : pageSize;
                const hasMorePages = totalElements > 0 && currentPage < totalPages - 1;
                
                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: currentPage,
                        pageSize: actualPageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: hasMorePages,
                    },
                }));
            } else if (tabKey === "ict") {
                // Handle ICT - use ICT API
                const res = await getAllICT(pageNumber, pageSize);
                const pageData = res.data?.data || res.data || {};
                allAssets = pageData.content || pageData.data || pageData || [];
                const pagination = res.data?.pagination || {};
                // IMPORTANT: API returns snake_case (total_elements, total_pages) not camelCase
                // Support both formats for compatibility
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : (pagination.totalElements !== undefined && pagination.totalElements !== null
                            ? pagination.totalElements
                            : (pagination.total_elements !== undefined && pagination.total_elements !== null
                                ? pagination.total_elements
                                : 0)));
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (totalElements > 0 ? Math.ceil(totalElements / pageSize) : 1));
                const currentPage = pageData.number !== undefined ? pageData.number : (pageData.page !== undefined ? pageData.page : pageNumber);
                const actualPageSize = pageData.size !== undefined ? pageData.size : pageSize;
                const hasMorePages = totalElements > 0 && currentPage < totalPages - 1;
                
                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: currentPage,
                        pageSize: actualPageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: hasMorePages,
                    },
                }));
            } else if (tabKey === "land") {
                // Handle land - use Land API
                const res = await getAllLand(pageNumber, pageSize);
                const pageData = res.data?.data || res.data || {};
                allAssets = pageData.content || pageData.data || pageData || [];
                const pagination = res.data?.pagination || {};
                // IMPORTANT: API returns snake_case (total_elements, total_pages) not camelCase
                // Support both formats for compatibility
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : (pagination.totalElements !== undefined && pagination.totalElements !== null
                            ? pagination.totalElements
                            : (pagination.total_elements !== undefined && pagination.total_elements !== null
                                ? pagination.total_elements
                                : 0)));
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (totalElements > 0 ? Math.ceil(totalElements / pageSize) : 1));
                const currentPage = pageData.number !== undefined ? pageData.number : (pageData.page !== undefined ? pageData.page : pageNumber);
                const actualPageSize = pageData.size !== undefined ? pageData.size : pageSize;
                const hasMorePages = totalElements > 0 && currentPage < totalPages - 1;
                
                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: currentPage,
                        pageSize: actualPageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: hasMorePages,
                    },
                }));
            } else if (tabKey === "machinery") {
                // Handle machinery - use Machinery API
                const res = await getAllMachinery(pageNumber, pageSize);
                const pageData = res.data?.data || res.data || {};
                allAssets = pageData.content || pageData.data || pageData || [];
                const pagination = res.data?.pagination || {};
                // IMPORTANT: API returns snake_case (total_elements, total_pages) not camelCase
                // Support both formats for compatibility
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : (pagination.totalElements !== undefined && pagination.totalElements !== null
                            ? pagination.totalElements
                            : (pagination.total_elements !== undefined && pagination.total_elements !== null
                                ? pagination.total_elements
                                : 0)));
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (totalElements > 0 ? Math.ceil(totalElements / pageSize) : 1));
                const currentPage = pageData.number !== undefined ? pageData.number : (pageData.page !== undefined ? pageData.page : pageNumber);
                const actualPageSize = pageData.size !== undefined ? pageData.size : pageSize;
                const hasMorePages = totalElements > 0 && currentPage < totalPages - 1;
                
                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: currentPage,
                        pageSize: actualPageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: hasMorePages,
                    },
                }));
            } else if (tabKey === "vehicles") {
                // Handle vehicles - use Vehicles API
                const res = await getAllVehicles(pageNumber, pageSize);
                const pageData = res.data?.data || res.data || {};
                allAssets = pageData.content || pageData.data || pageData || [];
                const pagination = res.data?.pagination || {};
                // IMPORTANT: API returns snake_case (total_elements, total_pages) not camelCase
                // Support both formats for compatibility
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : (pagination.totalElements !== undefined && pagination.totalElements !== null
                            ? pagination.totalElements
                            : (pagination.total_elements !== undefined && pagination.total_elements !== null
                                ? pagination.total_elements
                                : 0)));
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (totalElements > 0 ? Math.ceil(totalElements / pageSize) : 1));
                const currentPage = pageData.number !== undefined ? pageData.number : (pageData.page !== undefined ? pageData.page : pageNumber);
                const actualPageSize = pageData.size !== undefined ? pageData.size : pageSize;
                const hasMorePages = totalElements > 0 && currentPage < totalPages - 1;
                
                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: currentPage,
                        pageSize: actualPageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: hasMorePages,
                    },
                }));
            } else if (tabKey === "infrastructure") {
                // Handle infrastructure - use INFR category code API
                const res = await getAllInfrastructure(pageNumber, pageSize);
                const pageData = res.data?.data || res.data || {};
                allAssets = pageData.content || pageData.data || pageData || [];
                const pagination = res.data?.pagination || {};
                totalElements = pageData.totalElements !== undefined && pageData.totalElements !== null
                    ? pageData.totalElements
                    : (pageData.total_elements !== undefined && pageData.total_elements !== null
                        ? pageData.total_elements
                        : (pagination.totalElements !== undefined && pagination.totalElements !== null
                            ? pagination.totalElements
                            : (pagination.total_elements !== undefined && pagination.total_elements !== null
                                ? pagination.total_elements
                                : 0)));
                const totalPages = pageData.totalPages !== undefined && pageData.totalPages !== null
                    ? pageData.totalPages
                    : (pageData.total_pages !== undefined && pageData.total_pages !== null
                        ? pageData.total_pages
                        : (totalElements > 0 ? Math.ceil(totalElements / pageSize) : 1));
                const currentPage = pageData.number !== undefined ? pageData.number : (pageData.page !== undefined ? pageData.page : pageNumber);
                const actualPageSize = pageData.size !== undefined ? pageData.size : pageSize;
                const hasMorePages = totalElements > 0 && currentPage < totalPages - 1;

                setPaginationByTab((prev) => ({
                    ...prev,
                    [tabKey]: {
                        pageNumber: currentPage,
                        pageSize: actualPageSize,
                        totalElements: totalElements,
                        totalPages: totalPages,
                        hasMore: hasMorePages,
                    },
                }));
            }
            
            // Add reference_number and status aliases to all assets for display (same as export)
            allAssets = allAssets.map(asset => {
                // Extract valuation object from various possible locations (same as buildings)
                const valuation = asset.asset_valuation || asset.assetValuation || asset.valuation || {};
                
                // Get valuation reference from various possible locations
                const valuationRef = 
                    valuation.valuation_ref || 
                    valuation.valuationRef || 
                    valuation.reference_number ||
                    asset.valuation_ref || 
                    asset.valuationRef || 
                    "";
                
                // Get valuation status from various possible locations
                const valuationStatus = 
                    valuation.valuation_status || 
                    valuation.valuationStatus || 
                    valuation.status ||
                    asset.valuation_status || 
                    asset.valuationStatus || 
                    "";
                
                return {
                    ...asset,
                    // Add aliases for easier identification in display
                    reference_number: valuationRef,
                    status: valuationStatus,
                };
            });
            
            // Accumulate assets (append) or replace based on append flag
            setAssetsByCategory((prev) => {
                if (append) {
                    // Append new assets to existing ones
                    const existing = prev[tabKey] || [];
                    return { ...prev, [tabKey]: [...existing, ...allAssets] };
                } else {
                    // Replace with new assets (initial load or tab change)
                    return { ...prev, [tabKey]: allAssets };
                }
            });
            
            // Update pagination info (only if not already updated for specific categories)
            const categoriesWithDedicatedPagination = ["all", "siteworks", "buildings", "equipment", "furniture", "ict", "land", "machinery", "vehicles", "infrastructure"];
            if (!categoriesWithDedicatedPagination.includes(tabKey)) {
            setPaginationByTab((prev) => ({
                ...prev,
                [tabKey]: {
                        pageNumber: pageNumber,
                        pageSize: pageSize,
                        totalElements: totalElements,
                        totalPages: Math.ceil(totalElements / pageSize) || 1,
                        hasMore: pageNumber < Math.ceil(totalElements / pageSize) - 1,
                },
            }));
            }
        } catch (err) {
            console.error(`Failed to load ${tabKey} assets:`, err);
            if (!append) {
                setAssetsByCategory((prev) => ({ ...prev, [tabKey]: [] }));
            }
            setPaginationByTab((prev) => ({
                ...prev,
                [tabKey]: {
                    pageNumber: prev[tabKey]?.pageNumber || 0,
                    pageSize: DEFAULT_PAGE_SIZE,
                    totalElements: prev[tabKey]?.totalElements || 0,
                    totalPages: prev[tabKey]?.totalPages || 1,
                    hasMore: false,
                },
            }));
        } finally {
            if (append) {
                setLoadingMoreByCategory((prev) => ({ ...prev, [tabKey]: false }));
            } else {
                setLoadingByCategory((prev) => ({ ...prev, [tabKey]: false }));
            }
        }
    }, [assetIdParam, valuationIdParam]);

    // Fetch initial assets when tab changes or on mount
    useEffect(() => {
        // Reset and load first page when tab changes
        setAssetsByCategory((prev) => ({ ...prev, [activeTab]: [] }));
        setPaginationByTab((prev) => ({
            ...prev,
            [activeTab]: { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE, hasMore: true },
        }));
        setSearchTerm(""); // Clear search when changing tabs
        // Set loading state before fetching
        setLoadingByCategory((prev) => ({ ...prev, [activeTab]: true }));
        fetchAssetsByCategory(activeTab, 0, DEFAULT_PAGE_SIZE, false);
    }, [activeTab, fetchAssetsByCategory]);

    // Load more function
    const loadMore = useCallback(() => {
        const pagination = paginationByTab[activeTab];
        
        if (!pagination || !pagination.hasMore || loadingMoreByCategory[activeTab]) {
            return;
        }
        
        const nextPage = pagination.pageNumber + 1;
        fetchAssetsByCategory(activeTab, nextPage, pagination.pageSize, true);
    }, [activeTab, paginationByTab, loadingMoreByCategory, fetchAssetsByCategory]);

    const getVisibleSelectedAssets = useCallback(() => {
        return Array.from(selectedAssetsData.values());
    }, [selectedAssetsData]);

    const fetchCombineAssets = useCallback(async ({
        category = combineCategory,
        page = 0,
        append = false,
        size = 100,
        search = combineSearch,
    } = {}) => {
        setLoadingCombineAssets(true);
        try {
            const categoryCode = category === "all" ? undefined : CATEGORY_CODE_MAP[category];
            const normalizedSearch = String(search || "").trim();
            const useSearchEndpoint = Boolean(normalizedSearch) || !categoryCode;
            const response = useSearchEndpoint
                ? await searchAssets({
                    search: normalizedSearch || undefined,
                    categoryCode,
                    reviewed: true,
                    reviewNotCompleted: false,
                    page,
                    size,
                })
                : await getAssetsByCategory({
                    assetCategoryCode: categoryCode,
                    reviewed: true,
                    reviewNotCompleted: false,
                    page,
                    size,
                });
            const rows = getPageContent(response).filter((asset) => asset?.id);
            const meta = getPageMeta(response, size);
            setCombineAssets((previous) => append ? previous.concat(rows) : rows);
            setCombineAssetPage(page);
            setCombineAssetTotalPages(meta.totalPages || 1);
            setCombineAssetTotalElements(meta.totalElements || rows.length);
        } catch (error) {
            console.error("Failed to load combine assets:", error);
            enqueueSnackbar("Failed to load assets for combining.", { variant: "error" });
        } finally {
            setLoadingCombineAssets(false);
        }
    }, [combineCategory, combineSearch]);

    const handleLoadMoreCombineAssets = useCallback(() => {
        if (loadingCombineAssets || combineAssetPage >= combineAssetTotalPages - 1) {
            return;
        }
        fetchCombineAssets({ page: combineAssetPage + 1, append: true });
    }, [combineAssetPage, combineAssetTotalPages, fetchCombineAssets, loadingCombineAssets]);

    const handleLoadAllCombineAssets = useCallback(async () => {
        if (loadingAllCombineAssets) {
            return;
        }
        setLoadingAllCombineAssets(true);
        try {
            const size = 500;
            const categoryCode = combineCategory === "all" ? undefined : CATEGORY_CODE_MAP[combineCategory];
            const normalizedSearch = String(combineSearch || "").trim();
            const fetchPage = (pg) => (normalizedSearch || !categoryCode)
                ? searchAssets({
                    search: normalizedSearch || undefined,
                    categoryCode,
                    reviewed: true,
                    reviewNotCompleted: false,
                    page: pg,
                    size,
                })
                : getAssetsByCategory({
                    assetCategoryCode: categoryCode,
                    reviewed: true,
                    reviewNotCompleted: false,
                    page: pg,
                    size,
                });
            const firstResponse = await fetchPage(0);
            const firstRows = getPageContent(firstResponse).filter((asset) => asset?.id);
            const firstMeta = getPageMeta(firstResponse, size);
            let rows = firstRows;
            const totalPages = firstMeta.totalPages || 1;
            for (let page = 1; page < totalPages; page += 1) {
                const response = await fetchPage(page);
                rows = rows.concat(getPageContent(response).filter((asset) => asset?.id));
            }
            setCombineAssets(rows);
            setCombineAssetPage(Math.max(0, totalPages - 1));
            setCombineAssetTotalPages(totalPages);
            setCombineAssetTotalElements(firstMeta.totalElements || rows.length);
        } catch (error) {
            console.error("Failed to load all combine assets:", error);
            enqueueSnackbar("Failed to load all assets for this category.", { variant: "error" });
        } finally {
            setLoadingAllCombineAssets(false);
        }
    }, [combineCategory, combineSearch, loadingAllCombineAssets]);

    const handleToggleAssetSelection = useCallback((asset) => {
        const assetId = asset.id;
        setSelectedAssetIds((previous) => {
            const next = new Set(previous);
            if (next.has(assetId)) {
                next.delete(assetId);
            } else {
                next.add(assetId);
            }
            return next;
        });
        setSelectedAssetsData((previous) => {
            const next = new Map(previous);
            if (next.has(assetId)) {
                next.delete(assetId);
            } else {
                next.set(assetId, asset);
            }
            return next;
        });
    }, []);

    const handleSelectVisibleAssets = useCallback((assets) => {
        setSelectedAssetIds((previous) => {
            const next = new Set(previous);
            assets.forEach((asset) => {
                if (asset?.id) {
                    next.add(asset.id);
                }
            });
            return next;
        });
        setSelectedAssetsData((previous) => {
            const next = new Map(previous);
            assets.forEach((asset) => {
                if (asset?.id) {
                    next.set(asset.id, asset);
                }
            });
            return next;
        });
    }, []);

    const handleClearSelection = useCallback(() => {
        setSelectedAssetIds(new Set());
        setSelectedAssetsData(new Map());
    }, []);

    const handleOpenCombineInstitutionModal = useCallback(async () => {
        setCombineModalDismissed(false);
        setCombineInstitutionModalOpen(true);
        setCombineCategory(activeTab);
        fetchCombineAssets({ category: activeTab, page: 0, append: false });
        if (institutionOptions.length === 0) {
            setLoadingInstitutions(true);
            try {
                const institutions = await getAllAstInstitutions(1000);
                setInstitutionOptions(Array.isArray(institutions) ? institutions : []);
            } catch (error) {
                console.error("Failed to load institutions:", error);
                enqueueSnackbar("Failed to load institution names.", { variant: "error" });
            } finally {
                setLoadingInstitutions(false);
            }
        }
    }, [activeTab, fetchCombineAssets, institutionOptions.length]);

    const handleCombineCategoryChange = useCallback((option) => {
        const nextCategory = option?.value || "all";
        setCombineCategory(nextCategory);
        setSelectedAssetIds(new Set());
        setCombineInstitutionFilterId("");
        fetchCombineAssets({ category: nextCategory, page: 0, append: false });
    }, [fetchCombineAssets]);

    const handleCloseCombineInstitutionModal = useCallback(() => {
        setCombineModalDismissed(true);
        setCombineInstitutionModalOpen(false);
    }, []);

    const handleViewAssetBarcodes = useCallback(async (asset) => {
        setBarcodeModalAsset(asset);
        setBarcodeModalOpen(true);
        setLoadingBarcodeRows(true);
        try {
            const response = await getAssetBarcodes(asset.id);
            const rows = response?.data?.data || [];
            setBarcodeModalRows(Array.isArray(rows) ? rows : []);
        } catch (error) {
            console.error("Failed to load asset barcodes:", error);
            setBarcodeModalRows([]);
            enqueueSnackbar("Failed to load asset barcodes.", { variant: "error" });
        } finally {
            setLoadingBarcodeRows(false);
        }
    }, []);

    const handleCombineInstitution = useCallback(async () => {
        const selectedAssets = getVisibleSelectedAssets();
        if (selectedAssets.length < 2 || !selectedTargetInstitutionId) {
            enqueueSnackbar("Select assets and a target institution name.", { variant: "warning" });
            return;
        }
        setCombiningInstitution(true);
        try {
            const response = await combineAssetInstitution({
                assetIds: selectedAssets.map((asset) => asset.id),
                targetInstitutionId: selectedTargetInstitutionId,
            });
            enqueueSnackbar(response?.data?.message || "Institution names combined successfully.", { variant: "success" });
            setCombineInstitutionModalOpen(false);
            setSelectedTargetInstitutionId("");
            setSelectedAssetIds(new Set());
            setSelectedAssetsData(new Map());
            await fetchAssetsByCategory(activeTab, 0, DEFAULT_PAGE_SIZE, false);
        } catch (error) {
            console.error("Failed to combine institution names:", error);
            enqueueSnackbar(error?.response?.data?.message || "Failed to combine institution names.", { variant: "error" });
        } finally {
            setCombiningInstitution(false);
        }
    }, [activeTab, fetchAssetsByCategory, getVisibleSelectedAssets, selectedTargetInstitutionId]);

    useEffect(() => {
        if (!combineParam || combineInstitutionModalOpen || combineModalDismissed) {
            return;
        }
        const activeAssets = assetsByCategory[activeTab] || [];
        if (activeAssets.length > 0) {
            handleOpenCombineInstitutionModal();
        }
    }, [activeTab, assetsByCategory, combineInstitutionModalOpen, combineModalDismissed, combineParam, handleOpenCombineInstitutionModal]);

    useEffect(() => {
        if (!combineInstitutionModalOpen) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setSelectedAssetIds(new Set());
            setSelectedAssetsData(new Map());
            fetchCombineAssets({ category: combineCategory, page: 0, append: false, search: combineSearch });
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [combineCategory, combineInstitutionModalOpen, combineSearch, fetchCombineAssets]);

    // Filter assets based on search term
    const filterAssets = useCallback((assets) => {
        if (!searchTerm.trim()) return assets;
        
        const term = searchTerm.toLowerCase();
        return assets.filter(asset => {
            // Search in common fields
            const searchableFields = [
                asset.name,
                asset.building_name,           // Added for buildings
                asset.other_building_name,     // Added for "Others" buildings
                asset.otherBuildingName,       // Alternative field name
                asset.other_name,              // Added for other categories
                asset.otherName,               // Alternative field name
                asset.barcode,
                asset.description,
                asset.serial_number,
                asset.registration_number,
                asset.chassis_number,
                asset.make,
                asset.model,
                asset.plot_number,
                asset.building_number,
                asset.location,
                asset.institution_name,
                asset.observed_condition_name,
                asset.remark,
                asset.certificate_of_title,
                // Site Work specific fields
                asset.site_work_type,
                asset.other_specify,
                asset.dimensions,
            ];
            
            return searchableFields.some(field => 
                field && String(field).toLowerCase().includes(term)
            );
        });
    }, [searchTerm]);

    const filteredCombineAssets = useMemo(() => {
        return combineAssets.filter((asset) => {
            // Exclude incomplete or unreviewed assets
            const reviewed = asset?.is_reviewed ?? asset?.isReviewed;
            if (!(reviewed === true || reviewed === "true" || reviewed === 1 || reviewed === "1")) {
                return false;
            }
            const rnc = asset?.review_not_completed ?? asset?.reviewNotCompleted ?? asset?.is_review_not_completed ?? asset?.isReviewNotCompleted;
            if (rnc && rnc !== false && rnc !== "false" && rnc !== 0 && rnc !== "0") {
                return false;
            }

            const institutionLabel = formatInstitutionWithBracket(
                asset.institution_name || asset.institutionName || "",
                resolveInstitutionBracket(asset)
            );
            const barcode = getAssetPrimaryBarcode(asset);

            if (combineInstitutionFilterId && institutionLabel !== combineInstitutionFilterId) {
                return false;
            }
            if (combineBarcodeFilter === "with" && !barcode) {
                return false;
            }
            if (combineBarcodeFilter === "without" && barcode) {
                return false;
            }
            return true;
        });
    }, [combineAssets, combineBarcodeFilter, combineInstitutionFilterId]);


    const renderCategoryTabContent = (tabKey, TabComponent) => {
        const currentAssets = assetsByCategory[tabKey] || [];
        
        // Apply search filter
        const filteredAssets = filterAssets(currentAssets);
        
        const paginationInfo = paginationByTab?.[tabKey] || {
            pageNumber: 0,
            pageSize: DEFAULT_PAGE_SIZE,
            totalElements: 0,
            totalPages: 1,
            hasMore: false,
        };
        const isLoadingMore = loadingMoreByCategory[tabKey];
        const hasMore = paginationInfo.hasMore && !loadingByCategory[tabKey];

        // Add serial numbers to filtered assets
        const assetsWithSerial = filteredAssets.map((asset, index) => ({
            ...asset,
            serialNumber: index + 1, // 1-based indexing
        }));

        const isLoading = loadingByCategory[tabKey] === true;
        const hasNoAssets = currentAssets.length === 0;
        
        return (
            <TabPane tabId={tabKey}>
                {isLoading && hasNoAssets ? (
                    <div className="text-center py-5">
                        <Spinner
                            style={{
                                width: "3.5rem",
                                height: "3.5rem",
                                color: BRAND_BLUE,
                            }}
                        />
                        <p className="mt-3 text-muted">Loading {tabKey === "all" ? "all" : tabKey} assets...</p>
                    </div>
                ) : (
                    <>
                        {assetsWithSerial.length > 0 ? (
                            <>
                                <TabComponent 
                                    assets={assetsWithSerial} 
                                    loading={isLoading && hasNoAssets}
                                    onRefresh={() => fetchAssetsByCategory(tabKey, 0, DEFAULT_PAGE_SIZE, false)}
                                />
                                
                                {searchTerm.trim() && (
                                    <div className="text-center text-muted py-2">
                                        <small>Showing {assetsWithSerial.length} of {currentAssets.length} {tabKey} assets</small>
                                    </div>
                                )}
                                
                                <CategoryInfiniteScroll
                                    currentCount={currentAssets.length}
                                    totalCount={paginationInfo.totalElements}
                                    hasMore={hasMore}
                                    isLoading={loadingByCategory[tabKey]}
                                    isLoadingMore={isLoadingMore}
                                    onLoadMore={loadMore}
                                    categoryName={tabKey}
                                />
                            </>
                        ) : (
                            !loadingByCategory[tabKey] && (
                                <p className="text-center text-muted py-4">
                                    {searchTerm.trim() 
                                        ? `No ${tabKey} assets found matching "${searchTerm}"`
                                        : `No ${tabKey} assets found.`
                                    }
                                </p>
                            )
                        )}
                    </>
                )}
            </TabPane>
        );
    };

    // CSS styles for ALL categories - color coding by reference_number
    // This applies to all asset category tables (Buildings, Equipment, Furniture, ICT, Land, Machinery, Vehicles, Site Works)
    const categoryTableColorStyles = `
        table.table tbody tr.ref-group-0,
        table.table-sm tbody tr.ref-group-0,
        .table tbody tr.ref-group-0,
        .table-sm tbody tr.ref-group-0 {
            background-color: #e8f0f5 !important;
        }
        table.table tbody tr.ref-group-0 td,
        table.table-sm tbody tr.ref-group-0 td,
        .table tbody tr.ref-group-0 td,
        .table-sm tbody tr.ref-group-0 td {
            background-color: #e8f0f5 !important;
        }
        table.table tbody tr.ref-group-0:hover,
        table.table-sm tbody tr.ref-group-0:hover,
        .table tbody tr.ref-group-0:hover,
        .table-sm tbody tr.ref-group-0:hover {
            background-color: #d8e5ed !important;
        }
        table.table tbody tr.ref-group-0:hover td,
        table.table-sm tbody tr.ref-group-0:hover td,
        .table tbody tr.ref-group-0:hover td,
        .table-sm tbody tr.ref-group-0:hover td {
            background-color: #d8e5ed !important;
        }
        table.table tbody tr.ref-group-1,
        table.table-sm tbody tr.ref-group-1,
        .table tbody tr.ref-group-1,
        .table-sm tbody tr.ref-group-1 {
            background-color: #ffffff !important;
        }
        table.table tbody tr.ref-group-1 td,
        table.table-sm tbody tr.ref-group-1 td,
        .table tbody tr.ref-group-1 td,
        .table-sm tbody tr.ref-group-1 td {
            background-color: #ffffff !important;
        }
        table.table tbody tr.ref-group-1:hover,
        table.table-sm tbody tr.ref-group-1:hover,
        .table tbody tr.ref-group-1:hover,
        .table-sm tbody tr.ref-group-1:hover {
            background-color: #f5f5f5 !important;
        }
        table.table tbody tr.ref-group-1:hover td,
        table.table-sm tbody tr.ref-group-1:hover td,
        .table tbody tr.ref-group-1:hover td,
        .table-sm tbody tr.ref-group-1:hover td {
            background-color: #f5f5f5 !important;
        }
        .combine-modal-xl {
            max-width: 96vw !important;
        }
    `;

    if (isDataAnalysisComingSoon) {
        return <ComingSoon title="Data Analysis" />;
    }

    return (
        <React.Fragment>
            <style>{categoryTableColorStyles}</style>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs 
                        title="Assets" 
                        breadcrumbItem={`Assets By Category - ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`} 
                    />
                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardHeader>
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        {/* Search Input */}
                                        <div style={{ minWidth: "300px", maxWidth: "400px" }}>
                                            <InputGroup>
                                                <InputGroupText style={{ backgroundColor: "white" }}>
                                                    <i className="ri-search-line"></i>
                                                </InputGroupText>
                                                <Input
                                                    type="text"
                                                    placeholder={`Search ${activeTab}...`}
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={{ borderLeft: "none" }}
                                                />
                                                {searchTerm && (
                                                    <InputGroupText 
                                                        style={{ 
                                                            backgroundColor: "white",
                                                            cursor: "pointer",
                                                            borderLeft: "none"
                                                        }}
                                                        onClick={() => setSearchTerm("")}
                                                    >
                                                        <i className="ri-close-line"></i>
                                                    </InputGroupText>
                                                )}
                                            </InputGroup>
                                        </div>

                                        {/* Import (FURN/ICT) + Export */}
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            {(activeTab === "furniture" || activeTab === "ict" || activeTab === "equipment") && (
                                                <Button
                                                    color="success"
                                                    outline
                                                    onClick={() => setImportModalOpen(true)}
                                                    title={`Import ${CATEGORY_CODE_MAP[activeTab]} assets from Excel`}
                                                >
                                                    <i className="ri-upload-2-line me-1"></i>
                                                    Import Excel
                                                </Button>
                                            )}
                                        {assetsByCategory[activeTab]?.length > 0 && (
                                            <>
                                                <Button color="primary" onClick={handleOpenCombineInstitutionModal}>
                                                    <i className="ri-git-merge-line me-1"></i>
                                                    Combine Institutions
                                                </Button>
                                                <Button color="light" className="border shadow-none" onClick={() => setCombineHistoryModalOpen(true)} title="View Combine Log">
                                                    <i className="ri-history-line"></i>
                                                </Button>
                                                <Dropdown
                                                    isOpen={exportDropdownOpen}
                                                    toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
                                                >
                                                <DropdownToggle
                                                    caret
                                                    style={{
                                                        backgroundColor: BRAND_BLUE,
                                                        borderColor: BRAND_BLUE,
                                                        color: "white",
                                                    }}
                                                >
                                                    <i className="ri-download-line me-2"></i>
                                                    Export
                                                </DropdownToggle>
                                                <DropdownMenu>
                                                    <DropdownItem onClick={handleExportCSV}>
                                                        <i className="ri-file-text-line me-2"></i>
                                                        {activeTab === "all"
                                                            ? "Export as CSV"
                                                            : `Export ${getCategoryLabel(activeTab)} as CSV`}
                                                    </DropdownItem>
                                                    <DropdownItem onClick={handleExportExcel}>
                                                        <i className="ri-file-excel-line me-2"></i>
                                                        {activeTab === "all"
                                                            ? "Export as Excel"
                                                            : `Export ${getCategoryLabel(activeTab)} as Excel`}
                                                    </DropdownItem>
                                                    <DropdownItem onClick={handleExportPDF}>
                                                        <i className="ri-file-pdf-line me-2"></i>
                                                        {activeTab === "all"
                                                            ? "Export as PDF"
                                                            : `Export ${getCategoryLabel(activeTab)} as PDF`}
                                                    </DropdownItem>
                                                    {activeTab !== "all" && (
                                                        <>
                                                            <DropdownItem divider />
                                                            <DropdownItem onClick={handleOpenBarcodePdfModal}>
                                                                <i className="ri-barcode-box-line me-2"></i>
                                                                Download {getCategoryLabel(activeTab)} Barcode PDFs
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                    <DropdownItem divider />
                                                    <DropdownItem onClick={() => handleExportByValuation('csv')}>
                                                        <i className="ri-file-text-line me-2"></i>
                                                        Export by Valuation (CSV)
                                                    </DropdownItem>
                                                    <DropdownItem onClick={() => handleExportByValuation('excel')}>
                                                        <i className="ri-file-excel-line me-2"></i>
                                                        Export by Valuation (Excel)
                                                    </DropdownItem>
                                                    <DropdownItem onClick={() => handleExportByValuation('pdf')}>
                                                        <i className="ri-file-pdf-line me-2"></i>
                                                        Export by Valuation (PDF)
                                                    </DropdownItem>
                                                </DropdownMenu>
                                                </Dropdown>
                                            </>
                                        )}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody>

                                    <TabContent activeTab={activeTab}>
                                        {renderCategoryTabContent("all", AssetsTab)}
                                        {renderCategoryTabContent("buildings", BuildingsTab)}
                                        {renderCategoryTabContent("equipment", EquipmentTab)}
                                        {renderCategoryTabContent("furniture", FurnitureTab)}
                                        {renderCategoryTabContent("ict", ICTTab)}
                                        {renderCategoryTabContent("land", LandTab)}
                                        {renderCategoryTabContent("machinery", MachineryTab)}
                                        {renderCategoryTabContent("vehicles", VehiclesTab)}
                                        {renderCategoryTabContent("infrastructure", InfrastructureTab)}
                                        {renderCategoryTabContent("siteworks", SiteWorksTab)}
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Combine Institution Modal */}
                    <Modal
                        isOpen={combineInstitutionModalOpen}
                        toggle={handleCloseCombineInstitutionModal}
                        size="xl"
                        modalClassName="combine-modal-xl"
                    >
                        <ModalHeader toggle={handleCloseCombineInstitutionModal}>
                            <i className="ri-git-merge-line me-2" style={{ color: BRAND_BLUE }}></i>
                            Combine Institution Name
                        </ModalHeader>
                        <ModalBody>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                <span className="text-muted">
                                    Pick assets by category and filters, then choose one institution name for all of them.
                                </span>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={() => handleSelectVisibleAssets(filteredCombineAssets)}
                                        disabled={filteredCombineAssets.length === 0}
                                    >
                                        Select visible
                                    </Button>
                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={handleClearSelection}
                                        disabled={selectedAssetIds.size === 0}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>

                            <Row className="g-2 mb-3">
                                <Col md={3}>
                                    <small className="text-muted d-block mb-1">Category</small>
                                    <Select
                                        options={categorySelectOptions}
                                        value={selectedCombineCategoryOption}
                                        onChange={handleCombineCategoryChange}
                                        isDisabled={loadingCombineAssets || loadingAllCombineAssets}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted d-block mb-1">Institution filter</small>
                                    <Select
                                        options={combineInstitutionFilterOptions}
                                        value={selectedCombineInstitutionFilterOption}
                                        onChange={(option) => setCombineInstitutionFilterId(option?.value || "")}
                                        placeholder="All institutions"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Col>
                                <Col md={2}>
                                    <small className="text-muted d-block mb-1">Barcode</small>
                                    <Input
                                        type="select"
                                        value={combineBarcodeFilter}
                                        onChange={(event) => setCombineBarcodeFilter(event.target.value)}
                                    >
                                        <option value="all">All</option>
                                        <option value="with">With barcode</option>
                                        <option value="without">Without barcode</option>
                                    </Input>
                                </Col>
                                <Col md={4}>
                                    <small className="text-muted d-block mb-1">Search</small>
                                    <Input
                                        value={combineSearch}
                                        onChange={(event) => setCombineSearch(event.target.value)}
                                        placeholder="Asset, institution, barcode, location..."
                                    />
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                <div className="small text-muted">
                                    Showing {filteredCombineAssets.length} of {combineAssets.length} loaded
                                    {combineAssetTotalElements ? ` / ${combineAssetTotalElements} total` : ""}.
                                    {combineCategory === "buildings" && (
                                        <span className="ms-1 border-start ps-2 border-secondary ms-2">
                                            <span className="fw-medium">Buildings:</span> {filteredCombineAssets.filter(a => getAssetCategoryDisplay(a).toLowerCase().includes("building") || String(a.category_code || a.categoryCode).toUpperCase() === 'BLDG').length}
                                            <span className="mx-1">|</span>
                                            <span className="fw-medium">Sites:</span> {filteredCombineAssets.filter(a => getAssetCategoryDisplay(a).toLowerCase().includes("site") || String(a.category_code || a.categoryCode).toUpperCase() === 'SITE').length}
                                        </span>
                                    )}
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={handleLoadMoreCombineAssets}
                                        disabled={loadingCombineAssets || loadingAllCombineAssets || combineAssetPage >= combineAssetTotalPages - 1}
                                    >
                                        {loadingCombineAssets ? <Spinner size="sm" className="me-1" /> : null}
                                        Load more
                                    </Button>
                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={handleLoadAllCombineAssets}
                                        disabled={loadingCombineAssets || loadingAllCombineAssets || combineAssetPage >= combineAssetTotalPages - 1}
                                    >
                                        {loadingAllCombineAssets ? <Spinner size="sm" className="me-1" /> : null}
                                        Load all
                                    </Button>
                                </div>
                            </div>

                            <div className="border rounded mb-3" style={{ maxHeight: "55vh", overflow: "auto" }}>
                                <table className="table table-sm align-middle mb-0">
                                    <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                        <tr>
                                            <th style={{ width: 110 }}>Select</th>
                                            <th>Asset</th>
                                            <th>Category</th>
                                            <th>Institution</th>
                                            <th style={{ width: 80 }}>Qty</th>
                                            {combineCategory === "buildings" && <th>Buildings</th>}
                                            {combineCategory === "buildings" && <th>Sites</th>}
                                            <th>Ward</th>
                                            <th>Barcodes</th>
                                            <th style={{ width: 90 }}>View</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCombineAssets.map((asset) => {
                                            const selected = selectedAssetIds.has(asset.id);
                                            const barcode = getAssetPrimaryBarcode(asset);
                                            const quantity = getAssetQuantity(asset);
                                            const range = getBarcodeRange(barcode, quantity);
                                            const institutionLabel = formatInstitutionWithBracket(
                                                asset.institution_name || asset.institutionName || "",
                                                resolveInstitutionBracket(asset)
                                            );
                                            const assetName = getAssetDisplayName(asset);
                                            const categoryDisplay = getAssetCategoryDisplay(asset);
                                            const wardDisplay = getAssetWardDisplay(asset);
                                            return (
                                                <tr key={`combine-select-${asset.id}`} className={selected ? "table-primary" : ""}>
                                                    <td>
                                                        <Button
                                                            type="button"
                                                            color={selected ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="w-100"
                                                            onClick={() => handleToggleAssetSelection(asset)}
                                                        >
                                                            {selected ? "Selected" : "Select"}
                                                        </Button>
                                                    </td>
                                                    <td>
                                                        <div className="fw-medium">{assetName}</div>
                                                        <div className="small text-muted">{asset.description || asset.location || ""}</div>
                                                    </td>
                                                    <td>{categoryDisplay}</td>
                                                    <td>{institutionLabel}</td>
                                                    <td>{quantity}</td>
                                                    {combineCategory === "buildings" && <td>{asset.number_of_buildings || "-"}</td>}
                                                    {combineCategory === "buildings" && <td>{asset.site_works_count ?? (Array.isArray(asset.site_works) ? asset.site_works.length : (asset.has_site_work ? "Yes" : "0"))}</td>}
                                                    <td>{wardDisplay}</td>
                                                    <td>{range.display}</td>
                                                    <td>
                                                        <Button
                                                            type="button"
                                                            color="link"
                                                            size="sm"
                                                            className="p-0"
                                                            onClick={() => handleViewAssetBarcodes(asset)}
                                                        >
                                                            View
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filteredCombineAssets.length === 0 && (
                                            <tr>
                                                <td colSpan={8} className="text-center text-muted py-4">
                                                    {loadingCombineAssets ? "Loading assets..." : "No assets match the selected filters."}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {getVisibleSelectedAssets().length > 0 && (
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small className="text-muted">Selected Assets ({getVisibleSelectedAssets().length})</small>
                                        <small className="text-muted">Existing barcodes for these assets will be removed.</small>
                                    </div>
                                    <div className="border rounded p-2 bg-light d-flex flex-wrap gap-2" style={{ maxHeight: "150px", overflowY: "auto" }}>
                                        {getVisibleSelectedAssets().map(asset => {
                                            const assetName = getAssetDisplayName(asset);
                                            const institutionLabel = formatInstitutionWithBracket(
                                                asset.institution_name || asset.institutionName || "",
                                                resolveInstitutionBracket(asset)
                                            );
                                            const wardDisplay = getAssetWardDisplay(asset);
                                            const categoryDisplay = getAssetCategoryDisplay(asset);
                                            const quantity = getAssetQuantity(asset);
                                            const displayText = `${assetName} (${institutionLabel}${wardDisplay !== "-" ? `, ${wardDisplay}` : ""}) - ${categoryDisplay} - Qty: ${quantity}`;
                                            return (
                                                <span key={`selected-badge-${asset.id}`} className="badge bg-primary d-inline-flex align-items-center gap-1 p-2" style={{ whiteSpace: "normal", textAlign: "left", lineHeight: "1.4" }}>
                                                    {displayText}
                                                    <i 
                                                        className="ri-close-line ms-1" 
                                                        style={{ cursor: "pointer", fontSize: "16px" }}
                                                        onClick={() => handleToggleAssetSelection(asset)}
                                                        title="Remove from selection"
                                                    ></i>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <small className="text-muted d-block mb-1">Target institution name</small>
                            <Select
                                options={institutionSelectOptions}
                                value={selectedTargetInstitutionOption}
                                onChange={(option) => setSelectedTargetInstitutionId(option?.value || "")}
                                placeholder={loadingInstitutions ? "Loading institutions..." : "Choose institution name"}
                                isClearable
                                isDisabled={loadingInstitutions || combiningInstitution}
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({ ...base, minHeight: 38 }),
                                }}
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="light"
                                onClick={handleCloseCombineInstitutionModal}
                                disabled={combiningInstitution}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleCombineInstitution}
                                disabled={combiningInstitution || !selectedTargetInstitutionId}
                            >
                                {combiningInstitution ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-line me-1"></i>}
                                Combine
                            </Button>
                        </ModalFooter>
                    </Modal>

                    <Modal
                        isOpen={barcodeModalOpen}
                        toggle={() => setBarcodeModalOpen(false)}
                        size="lg"
                    >
                        <ModalHeader toggle={() => setBarcodeModalOpen(false)}>
                            Barcodes for {barcodeModalAsset ? getAssetDisplayName(barcodeModalAsset) : "Asset"}
                        </ModalHeader>
                        <ModalBody>
                            {loadingBarcodeRows ? (
                                <div className="text-center py-4">
                                    <Spinner size="sm" className="me-2" />
                                    Loading barcodes...
                                </div>
                            ) : (
                                <div className="border rounded" style={{ maxHeight: 420, overflow: "auto" }}>
                                    <table className="table table-sm mb-0">
                                        <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                            <tr>
                                                <th>#</th>
                                                <th>Category</th>
                                                <th>Barcode</th>
                                                <th>Written At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {barcodeModalRows.map((row, index) => (
                                                <tr key={`${row.barcode}-${index}`}>
                                                    <td>{index + 1}</td>
                                                    <td>{row.category_code || row.categoryCode || "-"}</td>
                                                    <td>{row.barcode || "-"}</td>
                                                    <td>{row.barcode_written_at || row.barcodeWrittenAt || row.created_at || row.createdAt || "-"}</td>
                                                </tr>
                                            ))}
                                            {barcodeModalRows.length === 0 && (
                                                <tr>
                                                    <td colSpan={4} className="text-center text-muted py-4">
                                                        No barcode records found for this asset.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="light" onClick={() => setBarcodeModalOpen(false)}>
                                Close
                            </Button>
                        </ModalFooter>
                    </Modal>

                    {/* Column Selector Modal */}
                    <Modal 
                        isOpen={columnModalOpen} 
                        toggle={() => setColumnModalOpen(!columnModalOpen)}
                        size="lg"
                    >
                        <ModalHeader toggle={() => setColumnModalOpen(!columnModalOpen)}>
                            <i className="ri-list-check me-2" style={{ color: BRAND_BLUE }}></i>
                            Select Columns to Export - {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                        </ModalHeader>
                        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
                            {isFetchingAllData ? (
                                <div className="text-center py-4">
                                    <Spinner
                                        style={{
                                            width: "3rem",
                                            height: "3rem",
                                            color: BRAND_BLUE,
                                        }}
                                    />
                                    <p className="mt-3 text-muted">Fetching all data for export...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-3 d-flex align-items-center justify-content-between">
                                        <Button
                                            color="primary"
                                            size="sm"
                                            outline
                                            onClick={toggleAllColumns}
                                            style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}
                                        >
                                            <i className="ri-checkbox-multiple-line me-1"></i>
                                            Select / Deselect All
                                        </Button>
                                        <small className="text-muted">
                                            {(exportHeaders.length > 0 ? exportHeaders : getExportHeaders(activeTab)).length} columns available
                                        </small>
                                    </div>

                                    <Row>
                                        {(exportHeaders.length > 0 ? exportHeaders : getExportHeaders(activeTab)).map((header, index) => {
                                            const checkboxId = `column-${header.key}-${index}`;
                                            const isDefaultSelected =
                                                header.defaultSelected === undefined
                                                    ? true
                                                    : header.defaultSelected;
                                            return (
                                                <Col md={6} lg={4} key={checkboxId} className="mb-2">
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            id={checkboxId}
                                                            className="form-check-input"
                                                            name="export-column"
                                                            defaultChecked={isDefaultSelected}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor={checkboxId}
                                                            style={{ cursor: "pointer", userSelect: "none" }}
                                                        >
                                                            {header.label}
                                                        </label>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button 
                                color="secondary" 
                                onClick={() => setColumnModalOpen(false)}
                                disabled={isFetchingAllData}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={performExport}
                                disabled={isFetchingAllData}
                                style={{ 
                                    backgroundColor: BRAND_BLUE, 
                                    borderColor: BRAND_BLUE,
                                }}
                            >
                                <i className="ri-download-line me-2"></i>
                                Export {pendingExportType?.toUpperCase()}
                            </Button>
                        </ModalFooter>
                    </Modal>

                    {/* Barcode PDF Download Modal */}
                    <Modal
                        isOpen={barcodePdfModalOpen}
                        toggle={() => setBarcodePdfModalOpen(!barcodePdfModalOpen)}
                        size="lg"
                    >
                        <ModalHeader toggle={() => setBarcodePdfModalOpen(false)}>
                            <i className="ri-barcode-box-line me-2" style={{ color: BRAND_BLUE }}></i>
                            Barcode PDFs - {getCategoryLabel(activeTab)}
                        </ModalHeader>
                        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
                            {loadingBarcodePdfs ? (
                                <div className="text-center py-4">
                                    <Spinner
                                        style={{
                                            width: "3rem",
                                            height: "3rem",
                                            color: BRAND_BLUE,
                                        }}
                                    />
                                    <p className="mt-3 text-muted">Loading barcode PDFs...</p>
                                </div>
                            ) : activeCategoryBarcodePdfs.length === 0 ? (
                                <p className="text-muted mb-0">
                                    No barcode PDF files found for {getCategoryLabel(activeTab)}.
                                </p>
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {activeCategoryBarcodePdfs.map((file) => (
                                        <div
                                            key={file.id || file.filename}
                                            className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
                                        >
                                            <div className="me-3 text-truncate">
                                                <i className="ri-file-pdf-line me-2 text-danger"></i>
                                                <span className="fw-medium">{file.filename}</span>
                                            </div>
                                            <a
                                                href={file.downloadUrl || file.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-primary"
                                                style={{
                                                    backgroundColor: BRAND_BLUE,
                                                    borderColor: BRAND_BLUE,
                                                }}
                                            >
                                                <i className="ri-download-line me-1"></i>
                                                Download
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => setBarcodePdfModalOpen(false)}>
                                Close
                            </Button>
                        </ModalFooter>
                    </Modal>
                    {/* Combine History Modal */}
                    <CombineHistoryModal
                        isOpen={combineHistoryModalOpen}
                        toggle={() => setCombineHistoryModalOpen(!combineHistoryModalOpen)}
                    />

                    <AssetImportModal
                        isOpen={importModalOpen}
                        toggle={() => setImportModalOpen(false)}
                        initialCategoryCode={CATEGORY_CODE_MAP[activeTab] || "FURN"}
                        lockCategory={activeTab === "furniture" || activeTab === "ict" || activeTab === "equipment"}
                        onImported={() => {
                            setImportModalOpen(false);
                            // Refresh current category list from page 0
                            setAssetsByCategory((prev) => ({ ...prev, [activeTab]: [] }));
                            setPaginationByTab((prev) => ({
                                ...prev,
                                [activeTab]: { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE, hasMore: true },
                            }));
                            setLoadingByCategory((prev) => ({ ...prev, [activeTab]: true }));
                            fetchAssetsByCategory(activeTab, 0, DEFAULT_PAGE_SIZE, false);
                        }}
                    />

                </Container>
            </div>
        </React.Fragment>
    );
};

export default AssetsByCategory;
